import type { Dict } from "./index";

/** French copy. Must match en.ts key for key (TypeScript enforces it). */
const plural = (n: number, one: string, many: string) => (n <= 1 ? one : many);

export const fr: Dict = {
  lang: {
    label: "Choisir la langue",
    names: { fr: "Français", en: "English" },
  },

  format: {
    locale: "fr-FR",
    months: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    at: "à",
  },

  common: {
    somethingWrong: "Une erreur est survenue.",
    loading: "Chargement…",
    required: "Champ obligatoire",
    allAreas: "Toutes les villes",
    perDay: "/jour",
    perWeek: "/semaine",
    perWeekShort: "/sem.",
    perMonth: "/mois",
    verifiedCompany: "Loueur vérifié",
    vehiclesCount: (n) => `${n} ${n <= 1 ? "véhicule" : "véhicules"}`,
    noCity: "Pas encore de véhicule dans cette ville",
    companyLabel: (id) => `Loueur #${id}`,
    viewAll: "Voir tout",
    sponsored: "Sponsorisé",
    photo: "photo",
    email: "E-mail",
    emailPlaceholder: "vous@exemple.fr",
    ph: {
      firstName: "ex. Karim",
      lastName: "ex. Benali",
      phone: "ex. 06 12 34 56 78",
      password: "Votre mot de passe",
      newPassword: "Au moins 8 caractères",
      confirmPassword: "Retapez le mot de passe",
      companyName: "ex. VTC Mobility Paris",
      displayName: "Nom affiché aux chauffeurs",
      siret: "912 345 678 00021",
      vat: "FR00 000000000",
      street: "ex. 15 Avenue de la République",
      postalCode: "ex. 75011",
      city: "ex. Paris",
      region: "Région ou département (facultatif)",
      country: "ex. France",
      licenceNumber: "ex. 12AB34567",
      year: "ex. 2024",
      seats: "ex. 5",
      colour: "ex. Gris",
      mileage: "ex. 32000",
      description: "État, équipements, ce qui est inclus…",
      minDays: "ex. 7",
      kmIncluded: "ex. 1500",
      extraKm: "ex. 0,12",
      insurance: "ex. Assurance tous risques incluse, franchise 1 000 €.",
      maintenance: "ex. Entretien et pneus inclus. Véhicule de remplacement sous 48 h.",
    },
    password: "Mot de passe",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
    phone: "Téléphone",
    login: "Se connecter",
    logout: "Se déconnecter",
    back: "Retour",
    cancel: "Annuler",
    close: "Fermer",
    save: "Enregistrer",
    edit: "Modifier",
    publish: "Publier",
    send: "Envoyer",
    view: "Voir",
    remove: "Supprimer",
    quickActions: "Actions rapides",
    days: (n) => `${n} ${plural(n, "jour", "jours")}`,
    vehicles: (n) => `${n} ${plural(n, "véhicule", "véhicules")}`,
    availableUnits: (n) => `Disponible · ${n} ${plural(n, "véhicule", "véhicules")}`,
  },

  enums: {
    energy: {
      Electric: "Électrique",
      Hybrid: "Hybride",
      "Plug-in hybrid": "Hybride rechargeable",
      Diesel: "Diesel",
      Petrol: "Essence",
    },
    transmission: { Automatic: "Automatique", Manual: "Manuelle" },
    category: {
      Sedan: "Berline",
      "Premium sedan": "Berline premium",
      Estate: "Break",
      SUV: "SUV",
      Van: "Van",
    },
  },

  header: {
    nav: {
      vehicles: "Véhicules",
      howItWorks: "Comment ça marche",
      forCompanies: "Espace loueurs",
      pricing: "Tarifs loueurs",
      blog: "Blog",
    },
    companyArea: "Espace loueur",
    myAccount: "Mon compte",
    listVehicles: "Publier mes véhicules",
    menu: "Menu",
    toggleMenu: "Ouvrir ou fermer le menu",
    accountMenu: "Menu du compte",
    tabs: {
      aria: "Navigation rapide",
      home: "Accueil",
      findCar: "Trouver",
      listCar: "Louer ma voiture",
      howItWorks: "Comment ça marche",
    },
  },
  captcha: {
    required: "Veuillez compléter la vérification de sécurité.",
  },
  changeEmail: {
    change: "Modifier",
    newEmail: "Nouvelle adresse e-mail",
    password: "Mot de passe actuel",
    sendCode: "Envoyer le code",
    code: "Code reçu à la nouvelle adresse",
    confirm: "Confirmer",
    cancel: "Annuler",
    hint: "Nous envoyons un code à la nouvelle adresse. Votre e-mail ne change qu'une fois ce code saisi.",
  },
  notifications: {
    open: (n) => (n ? `Notifications, ${n} non lue${n > 1 ? "s" : ""}` : "Notifications"),
    title: "Notifications",
    markAll: "Tout marquer comme lu",
    empty: "Aucune notification pour le moment.",
    loadMore: "Voir les plus anciennes",
    loadError: "Impossible de charger vos notifications.",
    enablePush: "Être prévenu sur cet appareil",
    enablePushHint: "Demandes de réservation, réponses et messages, même quand Le Coin VTC est fermé.",
    enable: "Activer",
    pushOn: "Les notifications push sont activées sur cet appareil.",
    pushBlocked: "Les notifications sont bloquées dans les réglages de votre navigateur.",
    justNow: "À l'instant",
  },

  seo: {
    home: {
      title: "Location Voiture VTC en France – LE COIN VTC",
      description: "Trouvez une voiture VTC à louer à la semaine ou au mois. Comparez les offres de loueurs professionnels sur LE COIN VTC. Recherche gratuite.",
      ogTitle: "Location Voiture VTC en France – LE COIN VTC",
      ogDescription: "Trouvez une voiture VTC à louer à la semaine ou au mois. Comparez les offres de loueurs professionnels sur LE COIN VTC. Recherche gratuite.",
    },
    vehicles: {
      title: "Voitures VTC à Louer : Comparez les Offres – LE COIN VTC",
      description: "Découvrez les voitures VTC disponibles à la location. Filtrez par ville, prix, marque, motorisation et disponibilité. Sans frais pour les chauffeurs.",
      cityTitle: (city) => `Voitures VTC à louer à ${city} – LE COIN VTC`,
      cityDescription: (city) => `Voitures VTC disponibles à la location à ${city}. Filtrez par prix, marque, motorisation et disponibilité. Sans frais pour les chauffeurs.`,
    },
    vehicle: {
      title: (name, city) => `Location ${name} VTC à ${city}`,
      description: (name, city, weekly, monthly) =>
        `Louez une ${name} pour le VTC à ${city} : ${weekly}/semaine, ${monthly}/mois, chez un loueur vérifié. Demande de réservation gratuite sur Le Coin VTC.`,
    },
    company: {
      title: (name) => `${name} : loueur de véhicules VTC`,
      description: (name, count, area) =>
        `${name}, loueur VTC vérifié${area ? ` en ${area}` : ""} : ${count} véhicule${count > 1 ? "s" : ""} à louer, prix et avis des chauffeurs.`,
    },
    how: {
      title: "Comment Louer une Voiture VTC ? – LE COIN VTC",
      description: "Découvrez comment louer une voiture VTC ou publier vos véhicules sur LE COIN VTC. Inscription gratuite, demandes en ligne et réservation auprès des loueurs.",
    },
    partners: {
      title: "Louer ses Voitures à des Chauffeurs VTC – LE COIN VTC",
      description: "Vous êtes loueur de voitures VTC ? Publiez gratuitement vos annonces, gérez votre flotte et recevez des demandes de chauffeurs. 0 % de commission.",
      ogTitle: "Louer ses Voitures à des Chauffeurs VTC – LE COIN VTC",
      ogDescription: "Vous êtes loueur de voitures VTC ? Publiez gratuitement vos annonces, gérez votre flotte et recevez des demandes de chauffeurs. 0 % de commission.",
    },
    pricing: {
      title: "Tarifs Loueurs VTC : Annonces Gratuites – LE COIN VTC",
      description: "Profitez de l’offre de lancement LE COIN VTC : annonces illimitées, gestion de flotte et demandes de réservation sans abonnement ni commission.",
    },
    contact: {
      title: "Contact",
      description: "Une question sur une réservation, votre compte ou la publication de vos véhicules ? Contactez l'équipe Le Coin VTC.",
    },
    legal: { title: "Mentions légales", description: "Mentions légales de Le Coin VTC, la place de marché de location de véhicules VTC." },
    privacy: { title: "Politique de confidentialité", description: "Comment Le Coin VTC collecte, utilise et protège vos données personnelles, et comment exercer vos droits." },
    terms: { title: "Conditions générales d'utilisation", description: "Conditions générales d'utilisation de la place de marché Le Coin VTC pour les chauffeurs et les loueurs." },
    login: "Connexion",
    signup: "Créer un compte",
    companySignup: "Créer un compte loueur",
    forgotPassword: "Mot de passe oublié",
    resetPassword: "Nouveau mot de passe",
    verifyEmail: "Vérifiez votre e-mail",
    driverArea: "Espace chauffeur",
    companyArea: "Espace loueur",
    breadcrumbHome: "Accueil",
    breadcrumbVehicles: "Véhicules",
  },
  blog: {
    metaTitle: "Blog — Le Coin VTC",
    metaDescription: "Guides, coûts et réglementation pour les chauffeurs VTC qui louent un véhicule.",
    title: "Le blog Le Coin VTC",
    intro: "Guides, coûts et réglementation pour les chauffeurs VTC qui louent un véhicule.",
    all: "Tous",
    search: "Rechercher un article",
    empty: "Aucun article pour le moment.",
    error: "Impossible de charger les articles. Réessayez.",
    minutes: (n) => `${n} min de lecture`,
    back: "Tous les articles",
    notFound: "Cet article n'existe pas ou n'est plus publié.",
    notTranslated: "Cet article n'est disponible qu'en anglais.",
    related: "Articles liés",
    ctaTitle: "Vous cherchez un véhicule VTC ?",
    ctaText: "Comparez les véhicules de loueurs vérifiés, d'un jour à un mois.",
    ctaButton: "Voir les véhicules",
    band: "Blog",
    youAreHere: "Vous êtes ici :",
    home: "Accueil",
    by: "Par",
    in: "Dans",
    views: (n) => `${n.toLocaleString("fr-FR")} ${n > 1 ? "vues" : "vue"}`,
    faqTitle: "Questions fréquentes",
    tags: "Tags",
    tag: (t) => `Tag : ${t}`,
    clearFilter: "Retirer le filtre",
    pagination: "Pages du blog",
    previous: "Précédent",
    next: "Suivant",
    pageOf: (n, total) => `Page ${n} sur ${total}`,
    latest: "Derniers articles",
    popular: "Les plus lus",
    categories: "Catégories",
    archives: "Archives",
    findTitle: "Trouvez votre véhicule VTC",
    findText: "Comparez les locations à la semaine et au mois de loueurs vérifiés en Île-de-France.",
    findButton: "Voir les véhicules",
    authorRoles: {
      super_admin: "Admin de la plateforme Le Coin VTC",
      editor: "Équipe éditoriale Le Coin VTC",
      writer: "Équipe éditoriale Le Coin VTC",
    },
    ctas: {
      vehicles: { title: "Trouvez un véhicule VTC près de chez vous", text: "Locations à la semaine et au mois chez des loueurs vérifiés en Île-de-France.", button: "Voir les véhicules", href: "/vehicles" },
      list: { title: "Louez vos véhicules VTC", text: "Publiez votre flotte gratuitement et touchez des chauffeurs VTC vérifiés.", button: "Publier mes véhicules", href: "/partners" },
      companies: { title: "Gratuit pour les loueurs pendant le lancement", text: "0 % de commission, annonces illimitées, sans carte bancaire.", button: "Voir les tarifs loueurs", href: "/pricing" },
    },
  },
  footer: {
    faq: "FAQ",
    pricing: "Tarifs loueurs",
    blog: "Blog",
    legal: "Mentions légales",
    privacy: "Confidentialité",
    terms: "Conditions d’utilisation",
    contact: "Contact",
    prototype: "Prototype — sociétés et données fictives",
  },

  pricingPage: {
    title: "Tarifs de location pour les loueurs VTC",
    intro: "LE COIN VTC est entièrement gratuit pour tous les loueurs pendant notre période de lancement. Publiez vos annonces sans limite, profitez de 0 % de commission sur vos locations et accédez gratuitement à notre plateforme. Toujours gratuit pour les chauffeurs VTC.",
    launchBadge: "OFFRE DE LANCEMENT",
    launchTitle: "Accès gratuit pour les loueurs",
    launchPrice: "0 €",
    launchNote: "Sans abonnement, sans carte bancaire et sans commission.",
    launchCta: "Créer mon compte loueur",
    launchFeatures: [
      "Annonces actives illimitées",
      "Demandes de réservation illimitées",
      "Calendrier des disponibilités et gestion de flotte",
      "Messagerie avec les chauffeurs VTC",
      "Page publique du loueur avec avis clients",
      "Assistance par e-mail",
    ],
    laterTitle: "Formules payantes plus tard",
    laterText:
      "Des formules d’abonnement seront proposées après la période de lancement. Chaque loueur sera prévenu au moins 30 jours avant tout changement, et les annonces publiées pendant le lancement ne sont jamais supprimées.",
    sponsoredBadge: "Sponsorisé",
    sponsoredTitle: "Annonces sponsorisées, disponibles avec toutes les formules",
    sponsoredText: "Augmentez la visibilité de votre véhicule auprès des chauffeurs VTC : badge Sponsorisé et placement en tête des résultats.",
    sponsoredPrice: (days, price) => `${days} jours — ${price}`,
    footnote:
      "Les annonces sponsorisées sont un paiement unique, sans renouvellement automatique par défaut. Changez de formule à tout moment ; vos annonces existantes ne sont jamais supprimées.",
  },

  legalPages: {
    lastUpdated: "Dernière mise à jour : 1er octobre 2026",
    placeholder: "Texte provisoire du prototype. Il sera remplacé par la version finale validée par notre service juridique.",
    legal: {
      title: "Mentions légales",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Dernière mise à jour : 16 octobre 2026",
      intro: [
        "Ce site et la plateforme LE COIN VTC sont édités et exploités par ALVOS.",
      ],
      sections: [
        {
          title: "Éditeur",
          text: "LE COIN VTC est une plateforme éditée et exploitée par ALVOS, société par actions simplifiée (SAS) à capital variable de 2 000 €, avec un capital minimum de 100 €.\nSiège social : 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN : 928 450 063\nSIRET : 928 450 063 00013\nRCS : 928 450 063 R.C.S. Bobigny\nN° de TVA : FR51 928450063\nDirectrice de la publication : Malkiat Kaur\nContact : support@lecoinvtc.com",
        },
        {
          title: "Hébergement",
          text: "Le site LE COIN VTC est hébergé par :\nHébergeur : Namecheap\nDénomination sociale : Namecheap, Inc.\nAdresse du siège : 4600 East Washington Street, Suite 305, Phoenix, AZ 85034, États-Unis\nPays : États-Unis\nSite web : https://www.namecheap.com/\nContact : https://www.namecheap.com/support/",
        },
        {
          title: "Propriété intellectuelle",
          text: "Le nom LE COIN VTC, son logo, le design du site, ses logiciels, textes, éléments graphiques et autres contenus originaux disponibles sur la plateforme sont protégés par les lois applicables en matière de propriété intellectuelle.\nSauf mention contraire, ces éléments appartiennent à ALVOS ou sont utilisés avec l’autorisation de leurs titulaires respectifs.\nToute reproduction, diffusion, modification ou utilisation commerciale sans autorisation préalable est interdite, sauf dans les cas prévus par la loi.",
        },
        {
          title: "Responsabilité",
          text: "LE COIN VTC est une place de marché en ligne qui met en relation des chauffeurs VTC et des loueurs de véhicules.\nSauf mention contraire expresse, ALVOS n’est pas propriétaire des véhicules proposés sur la plateforme et n’en est pas le loueur.\nLes loueurs restent responsables de leurs annonces, de leurs prix, de leurs disponibilités, de leurs conditions de location, de leur assurance et des véhicules proposés sur la plateforme.\nLes chauffeurs restent responsables de l’exactitude des informations qu’ils fournissent, du respect des conditions d’éligibilité applicables et du contrat de location conclu avec le loueur.\nPour en savoir plus sur l’utilisation de la plateforme, consultez nos Conditions d’utilisation.",
        },
        {
          title: "Données personnelles et cookies",
          text: "Les données personnelles collectées via LE COIN VTC sont traitées conformément à notre Politique de confidentialité et à la réglementation applicable en matière de protection des données.\nLes informations relatives aux cookies et technologies similaires sont disponibles dans notre Politique cookies et dans les paramètres des cookies, le cas échéant.\nPour toute question relative à la protection de vos données, vous pouvez nous écrire à :\nE-mail : privacy@lecoinvtc.com",
        },
        {
          title: "Droit applicable",
          text: "Le présent site et les présentes mentions légales sont régis par le droit français.\nTout litige relève des juridictions compétentes conformément au droit français et européen applicable.",
        },
      ],
    },
    privacy: {
      title: "Politique de confidentialité",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Dernière mise à jour : 16 octobre 2026",
      intro: [
        "La présente Politique de confidentialité explique comment LE COIN VTC, plateforme éditée et exploitée par ALVOS, collecte, utilise, conserve et protège les données personnelles lorsque vous consultez notre site, créez un compte ou utilisez nos services de place de marché.",
        "Nous nous engageons à protéger votre vie privée et à traiter les données personnelles conformément à la réglementation française et européenne applicable en matière de protection des données, notamment le Règlement général sur la protection des données (RGPD).",
      ],
      // "## " lines are sub-headings, "- " lines bulleted lists
      sections: [
        {
          title: "Responsable du traitement",
          text: "Le responsable des traitements décrits dans la présente Politique de confidentialité est :\nALVOS, exploitant et éditeur de LE COIN VTC\nSiège social : 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN : 928 450 063\nSIRET : 928 450 063 00013\nRCS : 928 450 063 R.C.S. Bobigny\nN° de TVA : FR51 928450063\nContact données personnelles : privacy@lecoinvtc.com\nPour toute question concernant vos données personnelles ou vos droits, vous pouvez nous contacter à l’adresse ci-dessus.",
        },
        {
          title: "Données personnelles collectées",
          text: "Les informations que nous collectons dépendent de votre utilisation de LE COIN VTC et de votre statut : visiteur, chauffeur ou loueur.\n## Informations de compte\nLors de la création d’un compte, nous pouvons collecter :\n- Nom et prénom\n- Adresse e-mail\n- Numéro de téléphone\n- Mot de passe ou informations d’authentification\n- Type de compte\n- Informations de profil\n- Langue préférée\n- Date de création du compte\n- Statut du compte\n## Informations sur les chauffeurs\nPour les comptes Chauffeur, nous pouvons collecter les informations nécessaires pour établir et vérifier l’éligibilité, notamment :\n- Informations d’identité\n- Date de naissance, si nécessaire\n- Adresse postale\n- Informations relatives au permis de conduire\n- Documents professionnels VTC, le cas échéant\n- Pièces d’identité\n- Documents de vérification\n- Statut de vérification du compte\n- Autres documents nécessaires pour accéder à certains services de la place de marché\n## Informations sur les loueurs\nPour les loueurs, nous pouvons collecter :\n- Nom de la société ou de l’entreprise\n- Coordonnées du représentant légal ou de la personne de contact\n- Adresse professionnelle\n- SIREN, SIRET ou autres informations d’immatriculation\n- Documents d’immatriculation de la société\n- Coordonnées\n- Type d’activité\n- Type de compte\n- Documents de vérification\n- Informations de paiement ou de versement, le cas échéant\nLorsqu’un particulier exerce en tant que loueur, nous pouvons collecter les informations d’identification et professionnelles nécessaires à la vérification du compte.",
        },
        {
          title: "Données des annonces de véhicules",
          text: "Lorsqu’un loueur publie une annonce, nous pouvons traiter des informations telles que :\n- Marque et modèle du véhicule\n- Informations liées à l’immatriculation, si nécessaire\n- Photos du véhicule\n- Caractéristiques du véhicule\n- Prix de location\n- Caution\n- Disponibilités\n- Informations de retrait\n- Conditions de location\n- Informations d’assurance fournies par le loueur\n- Historique de création et de modification de l’annonce\nCertaines informations des annonces sont rendues publiques, car leur publication est nécessaire au fonctionnement de la place de marché.\nLes loueurs ne doivent pas publier d’informations personnelles inutiles dans les annonces publiques.",
        },
        {
          title: "Informations de réservation et de location",
          text: "Lorsque vous envoyez, recevez ou gérez une demande de réservation, nous pouvons traiter :\n- Les identifiants du chauffeur et du loueur\n- Le véhicule concerné\n- Les dates de location demandées\n- Le statut de la réservation\n- Le prix de location\n- Les informations relatives à la caution\n- L’historique des réservations\n- Les informations d’annulation\n- Le statut du paiement\n- Les messages liés à la réservation\n- Les informations de litige ou d’assistance\n- Les documents liés à la location, le cas échéant\nCes informations sont utilisées pour faire fonctionner la place de marché et faciliter les échanges entre chauffeurs et loueurs.",
        },
        {
          title: "Messages et communications",
          text: "Lorsque les utilisateurs communiquent via LE COIN VTC, nous pouvons traiter :\n- Les messages échangés entre utilisateurs\n- Les informations sur l’expéditeur et le destinataire\n- La date et l’heure des messages\n- Les pièces jointes\n- Les demandes d’assistance\n- Les réclamations\n- Les signalements concernant des utilisateurs ou des annonces\nNous pouvons accéder aux communications lorsque cela est raisonnablement nécessaire pour fournir une assistance, enquêter sur une fraude présumée, faire respecter nos Conditions d’utilisation, résoudre des problèmes signalés, protéger les utilisateurs ou respecter des obligations légales.\nLes utilisateurs ne doivent pas utiliser la messagerie pour transmettre des informations personnelles sensibles ou confidentielles inutiles.",
        },
        {
          title: "Données de localisation",
          text: "LE COIN VTC peut traiter des informations de localisation lorsque cela est nécessaire à certaines fonctionnalités de la place de marché.\nIl peut s’agir :\n- De la ville saisie lors d’une recherche de véhicule\n- Du lieu de retrait\n- De la localisation associée à une annonce\n- D’une localisation approximative déduite des critères de recherche\n- D’informations de localisation fournies volontairement par les utilisateurs\nSi une future fonctionnalité nécessite l’accès à la position précise de l’appareil d’un utilisateur, des informations et des autorisations appropriées seront fournies avant tout accès.\nNous ne suivons pas en continu les déplacements des utilisateurs, sauf si une fonctionnalité spécifique en informe clairement l’utilisateur et repose sur une base légale appropriée.",
        },
        {
          title: "Informations de paiement",
          text: "Lorsque des paiements, cautions, abonnements, services de mise en avant ou versements aux loueurs sont traités via LE COIN VTC, les services de paiement peuvent être fournis par un prestataire de paiement tiers agréé.\nSelon le service, ces informations peuvent inclure :\n- L’identité du payeur ou du bénéficiaire\n- Le montant de la transaction\n- Le statut du paiement\n- La date du paiement\n- Les informations de remboursement\n- Les informations de versement\n- Les coordonnées de facturation\n- Des informations limitées sur le moyen de paiement\nLes données complètes de carte bancaire sont normalement traitées directement par le prestataire de paiement agréé et ne sont pas conservées par LE COIN VTC.\nPrestataire de paiement : Stripe (https://stripe.com/fr/privacy)\nLe prestataire de paiement peut traiter des données personnelles selon ses propres conditions de confidentialité.",
        },
        {
          title: "Données techniques et d’utilisation",
          text: "Lorsque vous consultez ou utilisez LE COIN VTC, nous pouvons collecter automatiquement certaines informations techniques, notamment :\n- Adresse IP\n- Type d’appareil\n- Type et version du navigateur\n- Système d’exploitation\n- Historique des connexions\n- Date et heure d’accès\n- Pages ou fonctionnalités utilisées\n- Journaux techniques\n- Événements de sécurité\n- Informations d’erreur\n- Identifiants de cookies, le cas échéant\nNous utilisons ces informations pour la sécurité, la prévention de la fraude, le fonctionnement de la plateforme, la résolution de problèmes techniques et, lorsque cela est autorisé, la mesure d’audience.",
        },
        {
          title: "Pourquoi nous utilisons vos données personnelles",
          text: "Nous ne traitons des données personnelles que lorsque nous disposons d’une base légale valable.\n## Créer et gérer votre compte\nNous utilisons vos informations pour :\n- Créer votre compte\n- Vous authentifier\n- Gérer votre profil\n- Fournir les fonctionnalités du compte\n- Permettre l’accès aux fonctionnalités de la place de marché\nBase légale : exécution d’un contrat ou de mesures précontractuelles prises à votre demande.\n## Vérifier les utilisateurs\nNous pouvons utiliser des documents d’identification et professionnels pour :\n- Vérifier les chauffeurs\n- Vérifier les loueurs\n- Limiter les comptes frauduleux\n- Protéger les utilisateurs de la place de marché\n- Vérifier l’éligibilité à certains services\nBase légale : exécution du contrat de plateforme, intérêt légitime à assurer la sécurité de la place de marché et à prévenir la fraude, et obligations légales le cas échéant.\n## Fournir les services de la place de marché\nNous traitons des informations pour :\n- Publier les annonces de véhicules\n- Afficher les résultats de recherche\n- Envoyer et recevoir des demandes de réservation\n- Faciliter la communication\n- Gérer les réservations\n- Fournir les espaces utilisateurs\n- Assurer le fonctionnement de la plateforme\nBase légale : exécution d’un contrat.\n## Traiter les paiements\nLe cas échéant, des informations sont traitées pour :\n- Encaisser les sommes liées à la location\n- Traiter les frais de plateforme\n- Traiter les abonnements\n- Gérer les services de mise en avant payants\n- Faciliter les versements aux loueurs\n- Gérer les remboursements\nBase légale : exécution d’un contrat et respect des obligations financières, comptables ou fiscales applicables.\n## Prévenir la fraude et protéger la plateforme\nNous pouvons traiter des données pour :\n- Détecter les comptes suspects\n- Empêcher l’usage de documents frauduleux\n- Enquêter sur les abus de paiement\n- Protéger les utilisateurs\n- Sécuriser le site et les applications\n- Faire respecter nos Conditions d’utilisation\nBase légale : notre intérêt légitime à protéger LE COIN VTC, ses utilisateurs et l’intégrité de la place de marché.\n## Assurer le service client\nNous traitons les communications et les informations de compte pour répondre :\n- Aux demandes d’assistance\n- Aux réclamations\n- Aux problèmes techniques\n- Aux questions sur les comptes\n- Aux problèmes liés aux réservations\n- Aux signalements concernant des utilisateurs ou des annonces\nBase légale : exécution d’un contrat et notre intérêt légitime à fournir un service client efficace.\n## Respecter nos obligations légales\nNous pouvons traiter et conserver des informations lorsque cela est nécessaire pour :\n- La comptabilité\n- Les obligations fiscales\n- Les enquêtes pour fraude\n- Les procédures judiciaires\n- Les demandes des autorités de régulation\n- Les demandes des forces de l’ordre\n- Le respect de la réglementation applicable\nBase légale : obligation légale.\n## Communications marketing\nAvec votre consentement lorsqu’il est requis, nous pouvons vous envoyer des informations sur :\n- Les actualités de LE COIN VTC\n- Les nouvelles fonctionnalités de la place de marché\n- Des offres commerciales\n- Des promotions\n- Des services pertinents de la plateforme\nVous pouvez vous désabonner des communications marketing à tout moment.\nBase légale : consentement lorsque la réglementation l’exige.\nLes messages de service concernant votre compte, la sécurité, vos réservations ou des changements importants de la plateforme ne sont pas des communications marketing.",
        },
        {
          title: "Destinataires des données personnelles",
          text: "Nous ne vendons pas vos données personnelles.\nNous ne partageons des informations que lorsque cela est nécessaire, avec les catégories de destinataires suivantes.\n## Autres utilisateurs de la place de marché\nLes informations nécessaires à une réservation ou à un échange sur la place de marché peuvent être partagées entre un chauffeur et un loueur.\nPar exemple, à une étape appropriée de la réservation, des coordonnées, informations de profil ou de vérification pertinentes peuvent être communiquées lorsque cela est nécessaire à la location.\n## Prestataires de services\nNous pouvons faire appel à des prestataires de confiance pour :\n- L’hébergement du site\n- L’infrastructure cloud\n- L’envoi d’e-mails\n- Les SMS ou notifications\n- La vérification d’identité\n- Le traitement des paiements\n- Le service client\n- La cybersécurité\n- Le stockage des données\n- La mesure d’audience, le cas échéant\nCes prestataires ne peuvent traiter les données personnelles que dans la mesure nécessaire à la fourniture de leurs services et conformément aux exigences contractuelles et de protection des données applicables.\n## Autorités\nNous pouvons communiquer des données personnelles aux tribunaux, autorités de régulation, forces de l’ordre ou autres autorités compétentes lorsque la loi l’exige ou lorsque cela est juridiquement nécessaire pour constater, exercer ou défendre des droits en justice.\n## Conseillers professionnels\nSi nécessaire, des informations peuvent être communiquées à des avocats, experts-comptables, commissaires aux comptes, assureurs ou autres conseillers professionnels soumis à des obligations de confidentialité appropriées.",
        },
        {
          title: "Transferts internationaux de données",
          text: "Dans la mesure du possible, LE COIN VTC fait appel à des prestataires qui traitent les données personnelles en France ou dans l’Espace économique européen.\nToutefois, certains prestataires techniques peuvent traiter des données personnelles ou les rendre accessibles depuis des pays situés hors de l’Espace économique européen.\nLorsque de tels transferts ont lieu, nous utilisons si nécessaire un mécanisme juridique approprié, tel que :\n- une décision d’adéquation adoptée par la Commission européenne ;\n- des clauses contractuelles types ;\n- un autre mécanisme de transfert légalement reconnu ; ou\n- une autre garantie prévue par le RGPD.\nLes utilisateurs peuvent nous contacter pour obtenir plus d’informations sur les garanties applicables aux transferts internationaux de leurs données personnelles.",
        },
        {
          title: "Durées de conservation",
          text: "Nous ne conservons les données personnelles que le temps nécessaire aux finalités pour lesquelles elles ont été collectées et au respect de nos obligations légales.\nLes durées de conservation peuvent varier selon le type d’information et la finalité du traitement.\n## Données de compte\nLes informations de compte sont normalement conservées tant que le compte est actif et peuvent être conservées pendant une durée supplémentaire lorsque cela est nécessaire pour gérer des litiges, la fraude, des obligations contractuelles ou des réclamations juridiques.\n## Documents de vérification\nLes pièces d’identité et documents de vérification ne sont conservés que pendant la durée nécessaire à la vérification, à la sécurité, à la prévention de la fraude ou au respect des obligations légales applicables.\nLorsque le document original n’a plus besoin d’être conservé, il doit être supprimé ou faire l’objet d’un accès restreint.\n## Réservations et transactions\nLes informations relatives aux réservations, paiements et transactions peuvent être conservées pendant la durée nécessaire au respect des obligations contractuelles, comptables, fiscales, de prévention de la fraude et légales.\n## Messages et historique d’assistance\nLes communications peuvent être conservées pendant la durée nécessaire à la fourniture du service, au règlement des litiges, au maintien de la sécurité ou à la défense de droits en justice.\n## Journaux techniques\nLes journaux techniques et de sécurité sont conservés pendant une durée limitée, adaptée à la finalité pour laquelle ils sont collectés.\nLorsque les données ne sont plus nécessaires, elles sont supprimées, anonymisées ou archivées lorsque leur conservation est légalement requise.",
        },
        {
          title: "Clôture du compte et suppression des données",
          text: "La clôture d’un compte n’entraîne pas nécessairement la suppression immédiate de toutes les informations.\nCertaines informations peuvent devoir être conservées après la clôture du compte lorsque cela est nécessaire pour :\n- des locations ou réservations en cours ;\n- des paiements en attente ;\n- des litiges ;\n- la prévention de la fraude ;\n- la comptabilité et la fiscalité ;\n- des réclamations juridiques ; ou\n- le respect des obligations légales.\nLes informations qui n’ont plus de finalité légitime sont supprimées ou anonymisées conformément à nos procédures de conservation.",
        },
        {
          title: "Vos droits sur vos données",
          text: "Dans les conditions prévues par la réglementation applicable, vous pouvez disposer des droits suivants :\n## Droit d’accès\nObtenir la confirmation que nous traitons vos données personnelles et en obtenir une copie.\n## Droit de rectification\nDemander la correction de données personnelles inexactes ou incomplètes.\n## Droit à l’effacement\nDemander la suppression de vos données personnelles lorsque les conditions de suppression sont réunies.\n## Droit à la limitation\nDemander la limitation temporaire de certains traitements.\n## Droit à la portabilité\nRecevoir certaines données personnelles dans un format structuré, couramment utilisé et lisible par machine, lorsque ce droit s’applique.\n## Droit d’opposition\nVous opposer aux traitements fondés sur notre intérêt légitime, dans les cas prévus par la loi.\nVous pouvez vous opposer à la prospection commerciale à tout moment.\n## Droit de retirer votre consentement\nLorsqu’un traitement repose sur votre consentement, vous pouvez le retirer à tout moment.\nLe retrait du consentement ne remet pas en cause la licéité des traitements effectués avant ce retrait.",
        },
        {
          title: "Comment exercer vos droits",
          text: "Pour exercer un droit relatif à vos données, contactez-nous :\nE-mail : privacy@lecoinvtc.com\nAdresse postale : ALVOS – LE COIN VTC, 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nMerci de décrire clairement votre demande.\nNous pouvons demander des informations complémentaires lorsque cela est raisonnablement nécessaire pour vérifier votre identité avant de répondre à une demande portant sur des données personnelles.\nNous répondons dans le délai prévu par la réglementation applicable en matière de protection des données.",
        },
        {
          title: "Droit d’introduire une réclamation",
          text: "Si vous estimez que vos données personnelles ont été traitées en violation de la réglementation applicable, vous pouvez introduire une réclamation auprès de l’autorité française de protection des données :\nCommission Nationale de l'Informatique et des Libertés (CNIL), 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France\nVous pouvez également nous contacter au préalable afin que nous tentions de résoudre directement votre demande.",
        },
        {
          title: "Sécurité des données",
          text: "LE COIN VTC met en œuvre des mesures techniques et organisationnelles appropriées pour protéger les données personnelles contre :\n- l’accès non autorisé ;\n- la perte accidentelle ;\n- l’altération ;\n- la divulgation ;\n- l’utilisation abusive ;\n- la destruction ; et\n- le traitement illicite.\nCes mesures peuvent inclure des contrôles d’accès, l’authentification, des communications sécurisées, une surveillance technique, des sauvegardes et des restrictions d’accès aux informations personnelles.\nAucun système en ligne ne peut garantir une sécurité absolue, et les utilisateurs sont également responsables de la confidentialité de leurs mots de passe et identifiants.\nSi vous pensez que votre compte a été compromis, contactez immédiatement LE COIN VTC.",
        },
        {
          title: "Décisions automatisées",
          text: "LE COIN VTC n’a pas l’intention, à ce jour, de prendre des décisions produisant des effets juridiques ou des effets similaires significatifs sur la seule base d’un traitement automatisé, sauf information spécifique contraire des utilisateurs.\nDes outils automatisés peuvent néanmoins être utilisés pour faciliter :\n- la détection de la fraude ;\n- la surveillance de la sécurité ;\n- la prévention du spam ;\n- l’identification d’activités suspectes ;\n- la modération des annonces ; ou\n- l’optimisation de la place de marché.\nLorsque la réglementation applicable accorde des droits spécifiques en matière de décisions automatisées, ces droits sont respectés.",
        },
        {
          title: "Avis et informations publiques",
          text: "Certaines informations transmises à LE COIN VTC peuvent devenir publiques.\nIl peut s’agir :\n- Des informations de profil des loueurs\n- Des annonces de véhicules\n- Des photos de véhicules\n- Des identifiants publics attribués par la plateforme\n- Des avis et notes\n- D’autres informations expressément identifiées comme publiques\nLes utilisateurs ne doivent pas publier d’informations personnelles inutiles au fonctionnement de la place de marché.\nLE COIN VTC peut modérer ou supprimer des informations publiques lorsque la loi ou les Conditions d’utilisation l’exigent.",
        },
        {
          title: "Cookies et technologies similaires",
          text: "LE COIN VTC peut utiliser des cookies et technologies similaires à différentes fins.\n## Cookies strictement nécessaires\nIls peuvent servir à :\n- maintenir la connexion des utilisateurs ;\n- gérer les sessions de compte ;\n- mémoriser les paramètres de sécurité ;\n- protéger contre la fraude ;\n- mémoriser les préférences en matière de cookies ; et\n- assurer les fonctionnalités essentielles du site.\nCes technologies peuvent être utilisées sans consentement lorsque la réglementation applicable le permet.\n## Cookies de mesure d’audience\nLorsque les outils de mesure d’audience nécessitent un consentement, ils ne sont activés qu’après le consentement de l’utilisateur.\nCertaines technologies de mesure d’audience peuvent bénéficier d’une exemption lorsqu’elles respectent les exigences légales applicables.\n## Cookies publicitaires et marketing\nSi LE COIN VTC met en place des technologies de publicité, de reciblage, de suivi par les réseaux sociaux ou similaires nécessitant un consentement, elles ne seront activées qu’après un consentement valable de l’utilisateur.\nLes utilisateurs pourront refuser ou retirer leur consentement via les outils de gestion des cookies disponibles.\nPour plus d’informations, consultez notre Politique cookies et l’interface de paramétrage des cookies.",
        },
        {
          title: "Sites et services tiers",
          text: "LE COIN VTC peut contenir des liens vers des sites externes ou intégrer des services tiers.\nNous ne sommes pas responsables des pratiques de confidentialité de tiers indépendants.\nLes utilisateurs sont invités à consulter les politiques de confidentialité des services externes avant de leur communiquer des informations personnelles.",
        },
        {
          title: "Protection des mineurs",
          text: "LE COIN VTC est une place de marché professionnelle destinée aux adultes et aux entreprises.\nLa plateforme n’est pas destinée aux personnes de moins de 18 ans.\nNous ne créons pas sciemment de comptes pour des mineurs.\nSi nous apprenons qu’un mineur a fourni des données personnelles alors qu’un compte n’est pas autorisé, des mesures appropriées pourront être prises pour supprimer ou restreindre ces informations.",
        },
        {
          title: "Modifications de la présente Politique",
          text: "Nous pouvons mettre à jour la présente Politique de confidentialité lorsque :\n- les fonctionnalités de la place de marché évoluent ;\n- de nouveaux services sont lancés ;\n- nos pratiques de traitement des données changent ;\n- nos prestataires changent ; ou\n- la réglementation applicable évolue.\nLa version mise à jour affiche une nouvelle date de « Dernière mise à jour ».\nLorsqu’une modification affecte de manière importante le traitement de vos données personnelles, nous vous en informerons de manière complémentaire lorsque cela est requis.",
        },
        {
          title: "Nous contacter",
          text: "Pour toute question sur la présente Politique de confidentialité ou sur le traitement de vos données personnelles, contactez :\nLE COIN VTC, édité et exploité par ALVOS\n1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN : 928 450 063\nRCS : 928 450 063 R.C.S. Bobigny\nE-mail données personnelles : privacy@lecoinvtc.com",
        },
      ],
    },
    terms: {
      title: "Conditions d’utilisation",
      // Final text (no prototype banner), with its own date and introduction
      final: true,
      updated: "Dernière mise à jour : 16 octobre 2026",
      intro: [
        "Les présentes Conditions d’utilisation encadrent l’accès et l’utilisation du site, des applications et des services de place de marché LE COIN VTC.",
        "En créant un compte, en publiant une annonce de véhicule, en envoyant une demande de réservation ou en utilisant LE COIN VTC de toute autre manière, vous acceptez les présentes Conditions d’utilisation.",
        "LE COIN VTC est une plateforme éditée et exploitée par ALVOS, société par actions simplifiée (SAS), dont le siège est situé 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France, SIREN 928 450 063, RCS Bobigny 928 450 063.",
      ],
      // Lines starting with "- " are shown as a bulleted list
      sections: [
        {
          title: "Objet de la plateforme",
          text: "LE COIN VTC est une place de marché en ligne destinée à mettre en relation :\n- des chauffeurs VTC professionnels ou éligibles à la recherche d’un véhicule à louer ; et\n- des loueurs de véhicules professionnels ou autorisés proposant des véhicules adaptés à la location.\nLE COIN VTC fournit un service d’intermédiation numérique permettant aux utilisateurs de rechercher des véhicules, de publier des annonces, de communiquer, d’envoyer des demandes de réservation et, le cas échéant, d’utiliser des services de paiement ou des services associés à la place de marché.\nSauf mention contraire expresse, LE COIN VTC n’est ni le propriétaire, ni le vendeur, ni le loueur des véhicules présentés sur la plateforme.\nLe contrat de location du véhicule est conclu directement entre le chauffeur et le loueur.",
        },
        {
          title: "Conditions d’accès et comptes",
          text: "Les utilisateurs doivent fournir des informations exactes, complètes et à jour lors de la création et tout au long de l’utilisation de leur compte.\nUn utilisateur peut créer un compte en tant que :\n- Chauffeur : personne souhaitant louer un véhicule pour un usage professionnel autorisé.\n- Loueur : société, professionnel ou particulier autorisé, légalement habilité à proposer des véhicules à la location.\nLes utilisateurs sont responsables de la confidentialité de leurs identifiants et de toute activité réalisée depuis leur compte.\nUn compte ne peut être cédé, vendu ou partagé avec une autre personne sans l’autorisation de LE COIN VTC.\nLes visiteurs peuvent consulter les annonces de véhicules disponibles. Un compte Chauffeur éligible est nécessaire pour envoyer une demande de réservation ou accéder aux fonctionnalités réservées aux utilisateurs inscrits.",
        },
        {
          title: "Vérification des comptes et des documents",
          text: "La création d’un compte ne signifie pas que le compte est automatiquement vérifié.\nLE COIN VTC peut demander des documents après l’inscription afin de vérifier l’identité, le statut professionnel ou l’éligibilité d’un utilisateur.\nSelon le type de compte, les documents demandés peuvent inclure une pièce d’identité, des informations d’immatriculation de l’entreprise, des documents professionnels, des informations relatives au permis de conduire ou d’autres justificatifs.\nLes utilisateurs doivent fournir des documents authentiques, valides et à jour.\nLE COIN VTC peut refuser, restreindre ou suspendre la vérification lorsque les documents sont incomplets, expirés, incohérents, soupçonnés d’être frauduleux ou ne répondent pas aux exigences de la plateforme.\nLa vérification effectuée par LE COIN VTC ne constitue pas une garantie de la solvabilité d’un utilisateur, de l’état d’un véhicule, de sa conformité légale ou de son comportement futur.",
        },
        {
          title: "Annonces de véhicules",
          text: "Les loueurs sont seuls responsables de l’exactitude et de la légalité de leurs annonces.\nUne annonce doit décrire fidèlement le véhicule et comporter, le cas échéant, des informations telles que :\n- la marque et le modèle du véhicule ;\n- des photos ;\n- le prix de location ;\n- la caution ;\n- les disponibilités ;\n- la durée de location ;\n- les conditions de retrait ;\n- les conditions de kilométrage ;\n- les informations d’assurance ;\n- les conditions d’éligibilité ; et\n- toute condition de location supplémentaire.\nLes loueurs ne doivent pas publier d’annonces trompeuses, frauduleuses, en double ou illicites.\nUn loueur doit disposer du droit et de l’habilitation légale de proposer chaque véhicule publié depuis son compte.\nLE COIN VTC peut demander des informations complémentaires, corriger des erreurs d’affichage manifestes, refuser une annonce ou retirer une annonce qui ne respecte pas les présentes Conditions ou la réglementation applicable.",
        },
        {
          title: "Responsabilités liées au véhicule et au loueur",
          text: "Le loueur reste responsable du véhicule proposé sur la plateforme.\nLe loueur doit s’assurer, selon le cas, que le véhicule :\n- peut légalement être loué ;\n- est correctement immatriculé ;\n- dispose des documents nécessaires à son utilisation ;\n- est entretenu et en bon état de rouler ;\n- est couvert par une assurance adaptée à la location proposée et à l’usage prévu ; et\n- respecte les exigences légales et réglementaires applicables.\nLes conditions propres au véhicule, restrictions d’assurance, cautions, limites de kilométrage ou autres obligations doivent être communiquées au chauffeur avant la confirmation de la location.\nLE COIN VTC ne se substitue pas aux responsabilités légales, d’assurance, d’entretien ou contractuelles du loueur.",
        },
        {
          title: "Responsabilités du chauffeur",
          text: "Les chauffeurs doivent s’assurer qu’ils remplissent les conditions requises pour louer et utiliser un véhicule proposé.\nLes chauffeurs doivent fournir des informations exactes et les documents valides demandés par LE COIN VTC ou par le loueur.\nLe chauffeur est responsable :\n- de détenir un permis de conduire valide adapté au véhicule ;\n- de remplir les conditions professionnelles applicables à l’usage prévu du véhicule ;\n- de respecter le contrat de location ;\n- de respecter les conditions d’assurance ;\n- de payer les loyers, cautions et frais convenus ;\n- d’utiliser le véhicule de manière légale et responsable ; et\n- de restituer le véhicule selon les conditions de location convenues.\nUn chauffeur ne doit pas utiliser la plateforme ou un véhicule loué à des fins illicites, frauduleuses ou non autorisées.",
        },
        {
          title: "Réservation et contrat de location",
          text: "L’envoi d’une demande de réservation ne crée pas automatiquement un contrat de location.\nUne réservation n’est confirmée que lorsque le loueur accepte la demande et que les éventuelles conditions de réservation, paiements ou cautions requis ont été réalisés.\nLa relation de location finale lie le chauffeur et le loueur.\nLe loueur peut exiger que le chauffeur signe un contrat de location distinct avant ou lors de la remise du véhicule.\nLe contrat de location peut préciser des conditions supplémentaires, notamment :\n- la durée de location ;\n- l’échéancier de paiement ;\n- la caution ;\n- le kilométrage ;\n- l’assurance ;\n- les conducteurs autorisés ;\n- l’usage du véhicule ;\n- l’annulation ;\n- la restitution du véhicule ; et\n- la responsabilité en cas de dommages.\nEn cas de divergence portant sur la location du véhicule elle-même, le contrat de location conclu entre le chauffeur et le loueur demeure le document contractuel de référence pour cette location, sous réserve de la réglementation applicable.",
        },
        {
          title: "Prix, paiements et cautions",
          text: "Les loueurs fixent les prix de location de leurs véhicules, sauf mention contraire expresse de LE COIN VTC.\nLe prix de location, la caution et les éventuels frais supplémentaires doivent être affichés ou communiqués avant la confirmation de la réservation.\nLorsque LE COIN VTC propose une fonctionnalité de paiement en ligne, les paiements peuvent être traités par un prestataire de services de paiement tiers agréé.\nLE COIN VTC peut faciliter l’encaissement ou le transfert de sommes entre chauffeurs et loueurs sans devenir le loueur du véhicule.\nLes éventuels frais de plateforme, frais de réservation, frais d’abonnement ou services de mise en avant payants sont indiqués avant que l’utilisateur ne s’engage sur le service payant concerné.\nLes utilisateurs restent responsables des impôts, taxes et autres obligations liés à leur propre activité professionnelle.",
        },
        {
          title: "Annulations, remboursements et absences",
          text: "Les conditions d’annulation et de remboursement applicables à une location doivent être communiquées avant la confirmation de la réservation.\nLorsqu’un loueur fixe ses propres conditions d’annulation, il lui appartient de les communiquer clairement au chauffeur.\nSi LE COIN VTC met en place une politique d’annulation ou de remboursement commune à la plateforme, celle-ci sera affichée lors de la réservation et fera partie des conditions de réservation applicables.\nLes utilisateurs ne doivent pas effectuer de manière répétée des réservations qu’ils n’ont pas l’intention d’honorer.\nLE COIN VTC peut prendre des mesures à l’encontre des comptes présentant des réservations frauduleuses, des annulations injustifiées répétées, des abus de paiement ou toute autre utilisation abusive de la plateforme.",
        },
        {
          title: "Tarification des services LE COIN VTC",
          text: "Pendant la période de lancement, LE COIN VTC peut proposer tout ou partie de ses services standard gratuitement.\nLE COIN VTC peut ensuite mettre en place :\n- des formules d’abonnement ;\n- des limites au nombre d’annonces actives ;\n- des fonctionnalités premium ;\n- des options de mise en avant payantes ; ou\n- d’autres services de place de marché.\nTout prix applicable sera clairement affiché avant l’achat.\nLes modifications tarifaires ou contractuelles importantes concernant les utilisateurs professionnels existants seront communiquées à l’avance, conformément à la réglementation applicable et aux présentes Conditions.",
        },
        {
          title: "Classement des annonces et résultats de recherche",
          text: "Les annonces de véhicules peuvent être affichées selon des critères tels que :\n- la pertinence par rapport à la recherche de l’utilisateur ;\n- le lieu sélectionné ;\n- les caractéristiques du véhicule ;\n- le prix ;\n- les disponibilités ;\n- le niveau de complétude de l’annonce ;\n- la date de publication ou de mise à jour ; et\n- les autres filtres choisis par l’utilisateur.\nLE COIN VTC peut également proposer une visibilité payante, par exemple un placement « À la une » ou « Urgent ».\nLorsqu’un paiement influe sur la visibilité ou le classement d’un véhicule, le caractère promotionnel de l’annonce est clairement signalé.\nLe paiement d’une visibilité accrue ne constitue ni une recommandation, ni une certification, ni une garantie du loueur ou du véhicule par LE COIN VTC.",
        },
        {
          title: "Avis et notes",
          text: "LE COIN VTC peut permettre aux chauffeurs et aux loueurs de laisser des avis ou des notes à la suite d’échanges ou de locations.\nLes avis doivent :\n- porter sur une expérience réelle ;\n- être exacts et rédigés de bonne foi ;\n- ne contenir aucun propos diffamatoire, discriminatoire, menaçant ou illicite ;\n- ne divulguer aucune information confidentielle ou personnelle sans autorisation ; et\n- ne pas être manipulés au moyen de faux comptes ou d’avis artificiels.\nLE COIN VTC peut modérer, restreindre ou supprimer les avis qui enfreignent les présentes Conditions ou la réglementation applicable.",
        },
        {
          title: "Utilisations interdites",
          text: "Les utilisateurs ne doivent pas utiliser LE COIN VTC à des fins de fraude, d’usurpation d’identité, de falsification de documents, de location illicite de véhicules, d’abus de paiement, de harcèlement, de discrimination, de collecte non autorisée de données, de spam ou de toute autre activité illégale.\nLes utilisateurs ne doivent pas tenter de porter atteinte à la sécurité, à la disponibilité ou au fonctionnement de la plateforme.\nL’extraction automatisée de données, la copie non autorisée d’annonces, la création de faux comptes ou les tentatives de contournement des mesures de sécurité de la plateforme peuvent entraîner une restriction ou une suspension.",
        },
        {
          title: "Signalement d’annonces ou d’utilisateurs",
          text: "Les utilisateurs peuvent signaler une annonce, un compte, un avis ou tout autre contenu qu’ils estiment frauduleux, illicite, trompeur ou contraire aux présentes Conditions.\nLE COIN VTC peut examiner le contenu signalé et demander des justificatifs.\nSelon les circonstances, LE COIN VTC peut retirer un contenu, restreindre une annonce, demander des corrections, suspendre un compte ou prendre toute autre mesure proportionnée autorisée par la loi.",
        },
        {
          title: "Restriction, suspension et résiliation de compte",
          text: "LE COIN VTC peut restreindre, suspendre ou résilier un compte ou une annonce pour des motifs légitimes, notamment :\n- des informations fausses ou trompeuses ;\n- des documents frauduleux ;\n- des annonces illicites ;\n- le non-paiement des frais applicables ;\n- des annulations répétées ou des abus de réservation ;\n- l’utilisation abusive des avis ;\n- des menaces pour la sécurité ;\n- la violation des présentes Conditions ;\n- un risque pour les utilisateurs ou la plateforme ; ou\n- des exigences légales ou réglementaires.\nLorsque la réglementation l’exige, LE COIN VTC communique à l’utilisateur professionnel concerné le motif de la restriction, de la suspension ou de la résiliation, ainsi que la possibilité de déposer une réclamation.\nLes utilisateurs peuvent également demander la clôture de leur compte, sous réserve des réservations, paiements et litiges en cours et des obligations légales de conservation.",
        },
        {
          title: "Relation entre LE COIN VTC et les utilisateurs",
          text: "LE COIN VTC agit principalement en tant qu’intermédiaire mettant en relation chauffeurs et loueurs.\nSauf mention expresse pour un service particulier, LE COIN VTC :\n- n’est pas propriétaire des véhicules proposés ;\n- ne détermine pas l’état des véhicules ;\n- n’agit pas en tant que loueur des véhicules ;\n- n’emploie ni les chauffeurs ni les loueurs ; et\n- n’est pas automatiquement partie au contrat de location du véhicule.\nChaque loueur reste responsable de son véhicule, de son annonce, de son contrat de location, de ses obligations légales, de son assurance et du service fourni au chauffeur.\nChaque chauffeur reste responsable du respect du contrat de location et des exigences professionnelles et légales applicables.",
        },
        {
          title: "Disponibilité de la plateforme et responsabilité",
          text: "LE COIN VTC s’efforce de fournir une plateforme fiable mais ne garantit pas un accès ininterrompu ou exempt d’erreurs.\nLe service peut ponctuellement être indisponible en raison d’opérations de maintenance, de problèmes techniques, d’incidents de sécurité ou de circonstances échappant au contrôle raisonnable de LE COIN VTC.\nDans la mesure permise par la réglementation applicable, LE COIN VTC n’est pas responsable des pertes résultant uniquement :\n- d’informations inexactes fournies par un autre utilisateur ;\n- de l’état ou du fonctionnement d’un véhicule loué ;\n- du comportement d’un chauffeur ou d’un loueur ;\n- de l’annulation par un loueur ;\n- de litiges découlant du contrat de location ; ou\n- de circonstances échappant au contrôle raisonnable de LE COIN VTC.\nAucune disposition des présentes Conditions n’exclut ni ne limite la responsabilité lorsque cette exclusion ou limitation est interdite par la réglementation applicable.",
        },
        {
          title: "Propriété intellectuelle",
          text: "Le nom LE COIN VTC, son logo, le design de la plateforme, ses logiciels, textes, éléments graphiques et autres contenus originaux sont protégés par les lois applicables en matière de propriété intellectuelle.\nLes utilisateurs restent responsables des contenus qu’ils mettent en ligne, notamment les photos de véhicules, descriptions et informations professionnelles.\nEn publiant du contenu sur LE COIN VTC, les utilisateurs concèdent à LE COIN VTC une licence non exclusive d’hébergement, de reproduction, d’affichage et de traitement technique de ce contenu, dans la mesure nécessaire à l’exploitation et à la promotion de la place de marché.\nLes utilisateurs doivent disposer des droits nécessaires sur tout contenu qu’ils mettent en ligne.",
        },
        {
          title: "Données personnelles et confidentialité",
          text: "LE COIN VTC traite les données personnelles conformément à sa Politique de confidentialité et à la réglementation applicable en matière de protection des données.\nLes utilisateurs sont invités à consulter la Politique de confidentialité pour obtenir des informations sur :\n- les données personnelles collectées ;\n- les finalités du traitement ;\n- les bases légales ;\n- les destinataires des données ;\n- les durées de conservation ;\n- les cookies et technologies de suivi ; et\n- les droits des utilisateurs en matière de protection des données.\nLes utilisateurs ne doivent pas faire un usage abusif des informations personnelles obtenues via la place de marché.",
        },
        {
          title: "Réclamations et litiges",
          text: "En cas de litige entre un chauffeur et un loueur, les parties doivent d’abord tenter de le résoudre directement entre elles.\nLE COIN VTC peut apporter une assistance raisonnable ou fournir des outils de communication pour aider les utilisateurs à résoudre les problèmes liés à la place de marché, mais n’est pas tenue de trancher les litiges contractuels entre les parties, sauf disposition expresse d’une politique spécifique de la plateforme.\nLes réclamations concernant un compte LE COIN VTC, une annonce, une suspension, un paiement traité par la plateforme ou un service de la plateforme peuvent être adressées à :\nE-mail : support@lecoinvtc.com\nLes utilisateurs doivent fournir suffisamment d’informations pour identifier le compte, la réservation ou l’annonce concernés.",
        },
        {
          title: "Modification des présentes Conditions",
          text: "LE COIN VTC peut mettre à jour les présentes Conditions afin de tenir compte de l’évolution de la plateforme, de ses tarifs, de ses fonctionnalités, des exigences légales ou de son activité.\nLorsqu’une modification importante concerne les utilisateurs professionnels, LE COIN VTC les en informe à l’avance lorsque la réglementation l’exige.\nLes modifications rendues nécessaires de manière urgente pour des raisons légales, réglementaires, de prévention de la fraude, de cybersécurité ou de sécurité de la plateforme peuvent prendre effet plus tôt lorsque la loi le permet.\nLa poursuite de l’utilisation de la plateforme après la date d’entrée en vigueur des Conditions mises à jour vaut acceptation, lorsque la loi le permet.",
        },
        {
          title: "Droit applicable",
          text: "Les présentes Conditions sont régies par le droit français.\nTout litige relatif aux présentes Conditions ou à l’utilisation de LE COIN VTC relève des juridictions compétentes selon les règles de procédure françaises et européennes applicables, sauf disposition impérative contraire.\nAvant d’engager une procédure, les parties sont invitées à rechercher une solution amiable.",
        },
        {
          title: "Contact",
          text: "LE COIN VTC\nÉdité et exploité par ALVOS\nSiège social : 1 Avenue Aristide Briand, 93160 Noisy-le-Grand, France\nSIREN : 928 450 063\nSIRET : 928 450 063 00013\nRCS : 928 450 063 R.C.S. Bobigny\nN° de TVA : FR51 928450063\nE-mail : privacy@lecoinvtc.com",
        },
      ],
    },
  },

  contactPage: {
    title: "Contact",
    intro: "Une question sur votre compte, une annonce, une réservation ou l’utilisation de LE COIN VTC ? Notre équipe support est là pour vous aider.",
    response: "Nous nous efforçons de répondre à chaque demande sous un jour ouvré.",
    emailTitle: "E-mail",
    email: "support@lecoinvtc.com",
    emailNote: "Pour toute question sur votre compte, une réservation, une annonce, la vérification ou toute autre demande.",
    phoneTitle: "Téléphone",
    // Official business phone, e.g. "+33 1 23 45 67 89"; empty = the card shows the support hours only
    phone: "",
    hoursTitle: "Horaires du support",
    hours: "Du lundi au vendredi, 9 h – 18 h",
    holidays: "Hors jours fériés en France.",
    addressTitle: "Adresse",
    address: "LE COIN VTC\nÉdité et exploité par ALVOS\n1 Avenue Aristide Briand\n93160 Noisy-le-Grand\nFrance",
    emailCta: "Nous écrire",
    faqText: "De nombreuses réponses se trouvent aussi dans notre",
    faqLink: "FAQ",
  },

  home: {
    heroTitle: "Location de voiture VTC en Île-de-France",
    heroText: "Comparez les offres de location de voitures VTC hybrides et électriques auprès de loueurs professionnels. Location à la semaine ou au mois, sans frais pour les chauffeurs.",
    heroImageAlt: "Chauffeur VTC à côté d'une voiture électrique bleue de location",
    heroPhoto: "photo · VTC",
    search: {
      location: "Ville ou lieu de retrait",
      start: "Date de début",
      end: "Date de fin",
      submit: "Rechercher",
      submitAria: "Rechercher une voiture VTC disponible",
      endBeforeStart: "La date de fin doit être après la date de début.",
      pastDate: "Choisissez une date à partir d'aujourd'hui.",
    },
    popularEmpty: "Aucune voiture disponible pour le moment. Changez vos dates ou consultez toutes les offres.",
    popularError: "Impossible de charger les véhicules. Actualisez la page.",
    popularTitle: "Voitures VTC disponibles",
    viewAllVehicles: "Voir toutes les voitures VTC",
    categoriesTitle: "Location VTC par catégorie",
    // sub: shown until the backend lists the makes found in the category
    categories: {
      electric: { label: "Électrique", sub: "Tesla, Hyundai…" },
      hybrid: { label: "Hybride", sub: "Toyota, Kia, Hyundai…" },
      premium: { label: "Berline premium", sub: "Mercedes Classe E…" },
      estate: { label: "Break", sub: "Grand coffre, transferts aéroport" },
    },
    howTitle: "Comment louer une voiture VTC ?",
    steps: [
      { title: "Recherchez gratuitement", description: "Comparez les véhicules VTC, les tarifs, les disponibilités et les conditions de location sans créer de compte." },
      { title: "Envoyez votre demande", description: "Créez votre compte chauffeur gratuit, ajoutez vos documents et demandez la réservation du véhicule choisi." },
      { title: "Le loueur confirme", description: "Le loueur vérifie votre dossier et accepte votre demande. Le règlement s’effectue directement auprès de lui." },
    ],
    partner: {
      eyebrow: "Pour les professionnels de la location VTC",
      title: "Publiez vos voitures VTC gratuitement. 0 % de commission.",
      listVehicles: "Déposer mes annonces",
      seePricing: "Voir les tarifs",
      benefits: [
        { title: "Demandes qualifiées", description: "Recevez les demandes de chauffeurs VTC avec leurs informations et justificatifs professionnels." },
        { title: "Gestion de flotte", description: "Gérez les disponibilités de chaque véhicule et évitez les doubles réservations." },
        { title: "Annonces illimitées gratuites", description: "Profitez de notre offre de lancement : publiez vos véhicules sans abonnement ni commission." },
      ],
    },
  },

  companyPage: {
    breadcrumb: "Véhicules",
    ownerType: { company: "Société de location", individual: "Particulier" },
    memberSince: (year) => `Membre depuis ${year}`,
    reviewsCount: (n) => `${n} avis`,
    noReviews: "Pas encore d’avis",
    viewAll: (n) => `Voir les ${n} véhicules`,
    about: "À propos",
    lockedText:
      "Le nom, la description et les coordonnées du loueur sont visibles par les chauffeurs inscrits. Les notes, les avis et tous les véhicules sont publics.",
    unlock: "Débloquer les informations du loueur",
    stats: { listed: "Véhicules en ligne", available: "Disponibles aujourd’hui", fleet: "Voitures dans la flotte" },
    infoTitle: "Informations du loueur",
    publicId: "Identifiant public",
    area: "Zone",
    name: "Nom de la société",
    address: "Adresse",
    contact: "Téléphone et e-mail",
    locked: "Visible par les chauffeurs inscrits",
    memberSinceLabel: "Membre depuis",
    vehiclesTitle: (label) => `Véhicules de ${label}`,
    vehiclesSub: (listed, available) =>
      `${listed} ${listed <= 1 ? "véhicule" : "véhicules"} en ligne · ${available} disponibles aujourd’hui`,
    all: "Tous",
    availableOnly: "Disponibles uniquement",
    sort: { default: "Tri : par défaut", "price-asc": "Prix croissant", "price-desc": "Prix décroissant" },
    noVehicles: "Aucun véhicule ne correspond à ce filtre.",
    reviewsTitle: "Avis clients",
    reviewsNote: "Les avis proviennent de chauffeurs ayant loué chez ce loueur et sont vérifiés par notre équipe avant publication.",
    rented: (vehicle) => `A loué ${vehicle}`,
    noReviewsText: "Ce loueur n’a pas encore d’avis publiés.",
    notFoundTitle: "Loueur introuvable",
    notFoundText: "Ce loueur n’existe pas ou ne propose pas de véhicule pour le moment.",
  },

  reviews: {
    title: "Avis",
    ratingFromCompanies: "Votre note donnée par les loueurs",
    ratingFromDrivers: "Votre note donnée par les chauffeurs",
    count: (n) => `${n} avis`,
    toWriteTitle: "En attente de votre avis",
    writtenTitle: "Vos avis publiés",
    receivedTitle: "Avis sur vous",
    noneWritten: "Vous n’avez encore publié aucun avis.",
    noneReceived: "Pas encore d’avis.",
    write: "Donner mon avis",
    edit: "Modifier",
    delete: "Supprimer",
    deleteConfirm: "Supprimer cet avis ? Cette action est définitive.",
    ratingLabel: "Note",
    ratingRequired: "Choisissez une note.",
    textRequired: "Écrivez au moins 10 caractères.",
    placeholderDriver: "Comment se sont passés le véhicule, la remise, la caution… ?",
    placeholderCompany: "Comment s’est passée la location avec ce chauffeur ?",
    pendingNote: "Visible par les autres après validation par notre équipe.",
    rejectedNote: "Non publié par notre équipe. Vous pouvez le modifier et le renvoyer.",
    submit: "Publier l’avis",
    saveEdit: "Enregistrer et envoyer pour validation",
    sending: "Envoi…",
    status: { pending: "En attente de validation", published: "Publié", rejected: "Non publié" },
  },

  driverPages: {
    home: {
      vtcExpiring: (date) => `Carte professionnelle VTC — expire le ${date}. Pensez à la renouveler.`,
      vtcExpired: (date) => `Carte professionnelle VTC — expirée le ${date}. Ajoutez votre nouvelle carte pour continuer à réserver.`,
      manageDocs: "Gérer mes documents",
      waiting: "En attente de la réponse du loueur.",
      docsRequired: "Le loueur a besoin de documents à jour. Consultez vos messages.",
      confirmedText: (code) => (code ? `Confirmée — le véhicule ${code} vous est réservé.` : "Confirmée — le loueur va vous attribuer un véhicule."),
      activeText: "Location en cours.",
      viewDetails: "Voir le détail",
      activityLine: (status, vehicle) => `${status} · ${vehicle}`,
    },
    bookings: {
      title: "Mes réservations",
      tabs: { all: "Toutes", requests: "En attente", upcoming: "Confirmées et en cours", past: "Historique" },
      empty: "Aucune réservation ici pour le moment.",
      find: "Trouver un véhicule",
      period: (start, end, days) => `${start} → ${end} · ${days} jours`,
      unread: (n) => `${n} ${n <= 1 ? "nouveau message" : "nouveaux messages"}`,
    },
    booking: {
      back: "Mes réservations",
      title: "Réservation",
      sent: (date) => `Demande envoyée le ${date}`,
      vehicle: "Véhicule",
      pickup: "Adresse de prise en charge",
      pickupHidden: "Communiquée une fois votre demande acceptée par le loueur.",
      company: "Loueur",
      period: "Période",
      rate: "Tarifs",
      estimate: "Prix estimé",
      deposit: "Caution",
      depositValue: (amount) => `${amount} · versée directement au loueur`,
      unit: "Véhicule attribué",
      contactTitle: "Contact du loueur",
      contactHidden: "Le téléphone, l’e-mail et l’adresse du loueur apparaissent ici une fois votre demande acceptée.",
      messagesTitle: "Messages",
      noMessages: "Pas encore de message. Posez une question au loueur sur cette réservation.",
      placeholder: "Écrire au loueur…",
      you: "Vous",
      platform: "Le Coin VTC",
      cancel: "Annuler ma demande",
      cancelConfirm: "Annuler cette demande de réservation ? Le loueur sera prévenu.",
      cancelled: "Demande annulée",
      review: "Donner mon avis",
      declined: (reason) => (reason ? `Refusée : ${reason}` : "Le loueur a refusé cette demande."),
      notFound: "Réservation introuvable.",
      history: "Historique",
    },
    messages: {
      title: "Messages",
      empty: "Pas encore de conversation. Les messages sur vos réservations apparaissent ici.",
      select: "Choisissez une conversation.",
      placeholder: "Écrire un message…",
    },
    favourites: {
      title: "Favoris",
      intro: "Les véhicules que vous avez enregistrés. Touchez le cœur sur la page d’un véhicule pour en ajouter ou en retirer.",
      empty: "Pas encore de favori.",
      remove: "Retirer",
    },
    profile: {
      title: "Profil",
      personal: "Informations personnelles",
      firstName: "Prénom",
      lastName: "Nom",
      email: "E-mail",
      emailHint: "Votre e-mail de connexion. Pour le changer, un code est envoyé à la nouvelle adresse.",
      phone: "Téléphone",
      address: "Adresse",
      city: "Ville",
      postalCode: "Code postal",
      language: "Langue des e-mails",
      professional: "Informations professionnelles",
      licenceNumber: "Numéro du permis de conduire",
      licenceDate: "Permis obtenu le",
      vtcCardNumber: "Numéro de carte VTC",
      vtcCardExpiry: "Expiration de la carte VTC",
      save: "Enregistrer",
      saving: "Enregistrement…",
      status: { pending: "Dossier chauffeur en vérification", verified: "Chauffeur vérifié", rejected: "Dossier chauffeur refusé", suspended: "Compte suspendu" },
    },
    request: {
      sending: "Envoi…",
      note: "Message au loueur (facultatif)",
      notePlaceholder: "Présentez votre activité (Uber, Bolt…), votre expérience, l’heure de prise en charge…",
    },
  },

  card: {
    from: "À partir de",
    monthlyAndDeposit: (monthly, deposit) => `${monthly}/mois · Caution : ${deposit}`,
    verifiedBy: (name) => `${name} · Loueur vérifié`,
    view: "Voir le véhicule",
    sponsored: "SPONSORISÉ",
  },

  vehicles: {
    area: "Zone",
    startDate: "Date de début",
    endDate: "Date de fin",
    filters: (n) => (n ? `Filtres · ${n}` : "Filtres"),
    hideFilters: "Masquer les filtres",
    // Heading of the results; the count shows beside it
    titleAll: "Voitures VTC à louer en Île-de-France",
    titleCity: (city) => `Voitures VTC à louer à ${city}`,
    dates: "Dates de location",
    sort: "Trier",
    sortOptions: {
      default: "Trier : pertinence",
      "price-asc": "Prix croissant",
      "price-desc": "Prix décroissant",
      available: "Les plus disponibles",
    },
    noMatchTitle: "Aucun véhicule ne correspond à votre recherche",
    noMatchText: "Modifiez vos filtres, choisissez une autre ville ou changez vos dates pour découvrir davantage de voitures VTC disponibles à la location.",
    resetFilters: "Réinitialiser les filtres",
    allAreas: "Toute l’Île-de-France",
  },

  filters: {
    title: "Filtres",
    reset: "Réinitialiser",
    availableOnly: "Disponible à mes dates",
    maxWeekly: "Prix maximum par semaine",
    maxDeposit: "Caution maximale",
    all: "Tous",
    minDuration: "Durée minimale de location",
    durations: {
      0: "Toutes les durées",
      7: "7 jours ou moins",
      14: "14 jours ou moins",
      30: "30 jours ou moins",
    },
    fuel: "Énergie",
    transmission: "Transmission",
    category: "Catégorie",
    make: "Marque",
  },

  vehicle: {
    prevPhoto: "Photo précédente",
    nextPhoto: "Photo suivante",
    notFoundTitle: "Véhicule introuvable",
    notFoundText: "Cette annonce a peut-être été retirée.",
    seeAll: "Voir tous les véhicules",
    allVehicles: "← Tous les véhicules",
    photoViews: ["Vue 3/4 avant", "Intérieur", "Tableau de bord", "Arrière", "Places arrière"],
    estimate: (days, basis) =>
      `${days} ${plural(days, "jour", "jours")} · ${{ day: "tarif journalier", week: "tarif hebdomadaire", month: "tarif mensuel" }[basis]}`,
    dateOrder: "Choisissez une date de fin postérieure à la date de début.",
    dateMax: (n) => `Une réservation va de 1 à ${n} jours. Envoyez une nouvelle demande pour une location plus longue.`,
    driverOnly: "Connectez-vous avec un compte chauffeur pour demander une réservation.",
    specs: {
      year: "Année",
      fuel: "Énergie",
      gearbox: "Boîte",
      seats: "Places",
      colour: "Couleur",
      mileage: "Kilométrage",
      category: "Catégorie",
      fleet: "Véhicules dans la flotte",
    },
    terms: {
      daily: "Prix par jour",
      weekly: "Tarif hebdomadaire",
      monthly: "Tarif mensuel",
      kmIncluded: "Kilométrage inclus",
      kmPerWeek: (km) => `${km} km / semaine`,
      extraKm: "Kilomètre supplémentaire",
      deposit: "Caution",
      depositValue: (amount) => `${amount} · versée au loueur`,
    },
    specsTitle: "Caractéristiques",
    termsTitle: "Conditions de location",
    insurance: "Assurance",
    maintenance: "Entretien",
    docsTitle: "Documents requis",
    docsHint: "Déposés une seule fois sur votre compte chauffeur et réutilisés pour vos prochaines demandes.",
    viewCompany: "Voir le profil du loueur",
    reviewsTitle: "Avis sur ce loueur",
    allReviews: (n) => `Voir les ${n} avis`,
    memberSince: (since) => `membre depuis ${since}`,
    companyMeta: (since, rating, reviews, hours) =>
      `membre depuis ${since} · ${String(rating).replace(".", ",")}/5 (${reviews} avis) · répond en moins de ${hours} h`,
    contactHint: "Les coordonnées du loueur vous sont communiquées une fois votre demande acceptée.",
    depositDirect: (amount) => `Caution ${amount} · versée directement au loueur`,
    start: "Début",
    end: "Fin",
    availability: (n, total) =>
      `${n} ${plural(n, "véhicule disponible", "véhicules disponibles")} sur ${total} à ces dates`,
    requestSent: (name) => `Demande envoyée à ${name}.`,
    notified: "Vous serez averti dès sa réponse.",
    requestBooking: "Demander une réservation",
    savedFavourite: "Ajouté aux favoris",
    addFavourite: "Ajouter aux favoris",
    disclaimer:
      "Aucun paiement sur la plateforme. Votre demande est soumise à l’accord du loueur ; les disponibilités affichées sont indicatives.",
    similar: "Véhicules similaires",
  },

  howPage: {
    title: "Comment fonctionne LE COIN VTC ?",
    intro: "LE COIN VTC est une marketplace spécialisée dans la location de voitures VTC. Les chauffeurs trouvent un véhicule adapté à leur activité et les loueurs professionnels proposent leurs voitures à la location.",
    driversTitle: "Chauffeurs VTC · 100 % gratuit",
    companiesTitle: "Loueurs professionnels · Gratuit au lancement",
    findVehicle: "Trouver une voiture VTC",
    companyArea: "Espace loueurs",
    faqTitle: "Questions fréquentes",
    driverSteps: [
      { title: "Recherchez sans inscription", text: "Trouvez une voiture VTC selon la ville, la motorisation, le tarif hebdomadaire ou mensuel et les disponibilités." },
      { title: "Demandez une réservation", text: "Créez votre compte chauffeur gratuit pour envoyer votre demande. Vos dates et le véhicule sélectionné sont conservés." },
      { title: "Ajoutez vos documents une seule fois", text: "Téléchargez votre pièce d’identité, votre permis de conduire, votre carte professionnelle VTC et votre justificatif de domicile." },
      { title: "Recevez la réponse du loueur", text: "Suivez l’acceptation, le refus ou la demande de complément depuis votre espace chauffeur. Le paiement s’effectue directement auprès du loueur." },
    ],
    companySteps: [
      { title: "Créez votre compte loueur", text: "Inscrivez-vous gratuitement et renseignez vos informations professionnelles. Ajoutez vos justificatifs pour faire vérifier votre compte." },
      { title: "Profitez de l’offre de lancement", text: "Publiez un nombre illimité d’annonces gratuitement, sans abonnement et sans commission sur vos locations." },
      { title: "Publiez vos véhicules VTC", text: "Créez vos annonces avec photos, tarifs, caution, conditions de location et disponibilités. Gérez plusieurs véhicules depuis votre espace loueur." },
      { title: "Gérez vos demandes", text: "Consultez les dossiers des chauffeurs, acceptez les réservations et attribuez les véhicules disponibles sans double réservation." },
    ],
    faq: [
      { q: "L’utilisation de LE COIN VTC est-elle gratuite pour les chauffeurs ?", a: "Oui. La création d’un compte chauffeur, la recherche de véhicules et l’envoi de demandes de réservation sont gratuits. Aucun frais de réservation n’est facturé par LE COIN VTC." },
      { q: "Comment payer la location et la caution ?", a: "Le loyer et la caution sont réglés directement auprès du loueur, selon les conditions prévues dans son offre. LE COIN VTC n’encaisse pas ces paiements." },
      { q: "Ma réservation est-elle confirmée immédiatement ?", a: "Non. Vous envoyez une demande de réservation. Le loueur examine votre dossier et peut accepter, refuser ou demander des informations complémentaires." },
      { q: "Quels documents faut-il fournir pour louer une voiture VTC ?", a: "Vous devez notamment fournir une pièce d’identité, un permis de conduire valide, une carte professionnelle VTC valide et un justificatif de domicile récent. Le loueur peut demander des documents complémentaires." },
    ],
  },

  forCompanies: {
    eyebrow: "Pour les professionnels de la location de voitures VTC",
    title: "Louez vos voitures VTC à des chauffeurs professionnels",
    subtitle: "Publiez gratuitement vos véhicules, recevez des demandes de chauffeurs VTC et développez votre activité de location. 0 % de commission sur vos locations.",
    start: "Commencer gratuitement",
    pricing: "Voir les tarifs",
    features: [
      { title: "Demandes de chauffeurs qualifiés", text: "Consultez le profil des chauffeurs VTC et le statut de leurs documents avant d’accepter une réservation." },
      { title: "Une annonce, plusieurs véhicules", text: "Publiez un modèle et gérez plusieurs véhicules avec leurs immatriculations, entretiens et disponibilités." },
      { title: "Aucune double réservation", text: "Chaque véhicule dispose de son propre calendrier pour éviter les réservations sur des périodes qui se chevauchent." },
      { title: "Gratuit et sans commission", text: "Pendant le lancement, publiez vos annonces sans abonnement. Vous encaissez directement vos loyers et conservez 100 % du montant." },
    ],
    notTitle: "Ce que LE COIN VTC ne fait pas",
    notText: "LE COIN VTC facilite la mise en relation entre loueurs et chauffeurs VTC. Le paiement des loyers et des cautions s’effectue directement entre les deux parties. La plateforme ne prélève aucune commission.",
    areas: "Zones recherchées par les chauffeurs",
  },

  auth: {
    roles: {
      driver: {
        tab: "Chauffeur",
        loginTitle: "Connexion",
        loginSubtitle: "Trouvez et réservez un véhicule VTC en quelques clics.",
        signupTitle: "Créer mon compte chauffeur",
        signupSubtitle: "Gratuit, sans abonnement ni frais de réservation.",
        signupLabel: "Créer un compte chauffeur",
        resetSubtitle:
          "Saisissez l’e-mail de votre compte chauffeur et nous vous enverrons un lien de réinitialisation.",
      },
      company: {
        tab: "Loueur",
        loginTitle: "Espace loueur",
        loginSubtitle: "Connectez-vous avec un compte loueur pour accéder à votre flotte.",
        signupTitle: "Créer un compte",
        signupSubtitle:
          "Pour les sociétés de location et les particuliers. Offre gratuite jusqu’à 3 annonces, 0 % de commission. Choisissez Particulier ou Société ci-dessous ; les documents de vérification s’ajoutent depuis votre compte.",
        signupLabel: "Créer un compte loueur",
        resetSubtitle:
          "Saisissez l’e-mail de votre compte loueur et nous vous enverrons un lien de réinitialisation.",
      },
    },
    accountType: "Type de compte",
    invalidCredentials: "E-mail ou mot de passe incorrect.",
    toasts: {
      loggedIn: (name: string) => `Bon retour, ${name} !`,
      signedUp: "Votre compte a été créé.",
      companySignedUp: "Compte créé. Ajoutez vos documents de vérification depuis votre compte pour pouvoir publier.",
      loggedOut: "Vous avez été déconnecté.",
    },
    enterEmail: "Saisissez votre e-mail.",
    rules: {
      emailRequired: "Saisissez votre e-mail.",
      emailInvalid: "Saisissez une adresse e-mail valide.",
      phoneRequired: "Saisissez votre numéro de téléphone.",
      phoneInvalid: "Saisissez un numéro de téléphone valide.",
      passwordRequired: "Choisissez un mot de passe.",
      passwordMin: "Utilisez au moins 8 caractères.",
      passwordPattern: "Utilisez au moins une lettre et un chiffre.",
    },
    login: {
      passwordRequired: "Saisissez votre mot de passe.",
      passwordMin: "Le mot de passe doit contenir au moins 6 caractères.",
      remember: "Se souvenir de moi",
      forgot: "Mot de passe oublié ?",
      submitting: "Connexion…",
      noAccount: "Pas encore de compte ?",
    },
    signup: {
      haveAccount: "Vous avez déjà un compte ?",
    },
    forgot: {
      title: "Réinitialiser votre mot de passe",
      checkInbox: "Consultez votre boîte de réception.",
      sent: (email) =>
        `Si un compte existe pour ${email}, vous recevrez un lien de réinitialisation d’ici quelques minutes. Pensez à vérifier vos spams.`,
      differentEmail: "Utiliser une autre adresse",
      sending: "Envoi…",
      submit: "Envoyer le lien",
    },
    reset: {
      title: "Choisissez un nouveau mot de passe",
      subtitle: "Au moins 8 caractères, avec une lettre et un chiffre.",
      newPassword: "Nouveau mot de passe",
      submit: "Enregistrer le mot de passe",
      submitting: "Enregistrement…",
      doneTitle: "Mot de passe modifié.",
      doneText: "Vous avez été déconnecté partout. Connectez-vous avec votre nouveau mot de passe.",
      missingTitle: "Ce lien est incomplet.",
      missingText: "Rouvrez le lien de l’e-mail, ou demandez-en un nouveau.",
      requestNew: "Recevoir un nouveau lien",
    },
    terms: {
      before: "J’accepte les ",
      termsLink: "conditions d’utilisation",
      middle: " et la ",
      privacyLink: "politique de confidentialité",
      after: ".",
      required: "Vous devez accepter les conditions pour continuer.",
    },
    driverSignup: {
      firstName: "Prénom",
      lastName: "Nom",
      firstNameRequired: "Saisissez votre prénom.",
      lastNameRequired: "Saisissez votre nom.",
      vtcCard: "Numéro de carte VTC",
      vtcCardPlaceholder: "ex. 075-2024-001234",
      vtcCardRequired: "Saisissez votre numéro de carte VTC.",
      confirmPassword: "Confirmer le mot de passe",
      confirmRequired: "Confirmez votre mot de passe.",
      mismatch: "Les mots de passe ne correspondent pas.",
      submitting: "Création du compte…",
      submit: "Créer mon compte",
      steps: ["Compte", "Identité", "Permis et VTC"],
      licenceDate: "Date de délivrance du permis",
      licenceDateRequired: "Saisissez la date d’obtention de votre permis.",
      licenceDateFuture: "Cette date est dans le futur.",
      emailPlaceholder: "prenom.nom@exemple.fr",
      mobile: "Mobile",
      mobileInvalid: "Numéro de mobile invalide",
      addressPlaceholder: "Numéro et rue",
      postcode: "Code postal",
      licenceNumber: "Numéro du permis de conduire",
      vtcCardNumberPlaceholder: "VTC-075-2024-0000",
      vtcCardExpiry: "Date d’expiration de la carte VTC",
      vtcCardExpired: "Votre carte VTC a expiré",
      continue: "Continuer",
      createAccount: "Créer mon compte",
      creating: "Création de votre compte…",
      codeSentTo: (email) => `Code de vérification envoyé à ${email}`,
      verifyTitle: "Vérifiez votre e-mail",
      verifyBefore: "Nous avons envoyé un code à 6 chiffres à ",
      verifyAfter: ". Saisissez-le pour activer votre compte.",
      codeLabel: "Code à 6 chiffres",
      codeRequired: "Saisissez le code à 6 chiffres reçu par e-mail.",
      resend: "Renvoyer le code",
      resending: "Envoi…",
      verify: "Vérifier l’e-mail",
      verifying: "Vérification…",
      nextNote:
        "Ensuite : ajoutez votre pièce d’identité, votre permis, votre carte VTC et un justificatif de domicile depuis votre espace chauffeur. Ils sont nécessaires avant votre première demande de réservation.",
      missingEmail: "Ouvrez cette page depuis l’inscription ou la connexion pour vérifier votre e-mail.",
    },
    companySignup: {
      codeNextNote:
        "Ensuite : ajoutez vos documents de vérification depuis votre compte. Notre équipe les vérifie avant la mise en ligne de vos annonces.",
      steps: ["Contact", "Société", "Documents"],
      stepOf: (current, total) => `Étape ${current} sur ${total}`,
      emailAddress: "Adresse e-mail",
      phoneNumber: "Numéro de téléphone",
      passwordPlaceholder: "Au moins 8 caractères",
      ownerType: "Type de compte",
      ownerTypeRequired: "Choisissez un type de compte",
      ownerTypes: {
        individual: {
          label: "Particulier",
          desc: "Vous louez votre ou vos propres véhicules en tant que particulier. Aucun document de société requis.",
        },
        company: {
          label: "Société",
          desc: "Une entreprise immatriculée (SIRET) qui loue des véhicules. Les documents de la société sont vérifiés.",
        },
      },
      individualNote: "Vous pourrez passer à un compte Société plus tard depuis les paramètres de votre compte.",
      companyNamePlaceholder: "Raison sociale",
      companyNameRequired: "Obligatoire pour un compte société",
      businessType: "Type d’activité",
      businessTypeRequired: "Choisissez votre type d’activité",
      businessTypeDescs: {
        "vtc-rental":
          "Sociétés spécialisées dans la location de véhicules aux chauffeurs VTC (Uber, Bolt et autres chauffeurs privés).",
        "individual-rental": "Particuliers qui louent leurs propres véhicules.",
        "rental-company": "Loueurs traditionnels qui louent des véhicules à des particuliers ou à des entreprises.",
      },
      createAccount: "Créer mon compte",
      creating: "Création de votre compte…",
      selectPlaceholder: "Sélectionner…",
      businessTypes: {
        "vtc-rental": "Location de voitures VTC",
        "individual-rental": "Location de voitures entre particuliers",
        "rental-company": "Société de location de voitures classique",
      },
      businessEmail: "E-mail professionnel",
      emailInvalid: "Adresse e-mail invalide",
      phoneInvalid: "Numéro invalide",
      passwordMin: "Au moins 8 caractères",
      companyName: "Raison sociale",
      siretPlaceholder: "14 chiffres",
      siretInvalid: "Le SIRET doit comporter 14 chiffres",
      vat: "N° de TVA (facultatif)",
      vatInvalid: "Numéro de TVA invalide",
      address: "Adresse",
      addressPlaceholder: "Commencez à saisir votre adresse",
      city: "Ville",
      postalCode: "Code postal",
      state: "Région",
      country: "Pays",
      fileTooLarge: "Fichier trop volumineux (10 Mo max)",
      fileType: "PDF, JPG ou PNG uniquement",
      docRequired: "Document obligatoire",
      docsHint: "PDF, JPG ou PNG, 10 Mo max par fichier.",
      continue: "Continuer",
      submitting: "Envoi…",
      submit: "Envoyer pour vérification",
      legalBefore: "En envoyant ce formulaire, vous acceptez les ",
      legalAfter:
        ". Le téléphone et l’e-mail ne sont communiqués aux chauffeurs qu’après acceptation d’une demande.",
    },
    documents: {
      kbis: { label: "Extrait Kbis", hint: "De moins de 3 mois" },
      insurance: { label: "Attestation d’assurance flotte", hint: "Couvrant l’activité de location VTC" },
      licence: { label: "Inscription au registre VTC / licence", hint: "Justificatif de l’activité de location VTC" },
      vehicleRegistration: { label: "Certificat d’immatriculation (carte grise)", hint: "Carte grise à votre nom" },
      vehicleInsurance: { label: "Attestation d’assurance du véhicule", hint: "Doit couvrir la location à des tiers" },
      vtcCard: { label: "Carte professionnelle VTC", hint: "En cours de validité" },
      drivingLicence: { label: "Permis de conduire", hint: "Recto verso" },
      idDocument: { label: "Pièce d’identité", hint: "Carte d’identité ou passeport" },
      proofOfAddress: { label: "Justificatif de domicile", hint: "De moins de 3 mois" },
      upload: "Téléverser",
      replace: "Remplacer",
      remove: (label) => `Supprimer ${label}`,
    },
  },

  driverArea: {
    area: "Espace chauffeur",
    findVehicle: "Trouver un véhicule",
    nav: {
      aria: "Espace chauffeur",
      dashboard: "Tableau de bord",
      bookings: "Mes réservations",
      documents: "Mes documents",
      messages: "Messages",
      favourites: "Favoris",
      reviews: "Avis",
      profile: "Profil",
    },
    home: {
      hello: (name) => `Bonjour ${name}`,
      intro: "Suivez vos demandes de réservation et gardez vos documents à jour.",
      completeTitle: "Complétez votre dossier chauffeur",
      completeText: (n) =>
        `Ajoutez vos ${n} documents une seule fois. Les loueurs en ont besoin pour accepter vos demandes.`,
      uploaded: (done, total) => `${done} / ${total} documents ajoutés`,
      uploadDocs: "Ajouter mes documents",
      sentTitle: "Dossier chauffeur envoyé",
      sentText: "Notre équipe vérifie vos documents. Vous pourrez envoyer des demandes une fois validés.",
      trackTitle: "Réservation à suivre",
      noRequest: "Aucune demande en cours.",
      findVtc: "Trouver un véhicule VTC",
      activity: "Activité récente",
      noActivity: "Rien pour le moment.",
      uploadedOn: (doc, date) => `${doc} ajouté le ${date}`,
      stats: {
        pending: "Demandes en attente",
        confirmed: "Confirmées ou en cours",
        approvedDocs: "Documents validés",
        favourites: "Favoris",
      },
    },
    documents: {
      title: "Mes documents",
      intro:
        "Ajoutés une fois, réutilisés pour chaque demande. Vos documents ne sont visibles que par les loueurs auxquels vous envoyez une demande et par l’équipe de modération.",
      notProvided: "Non fourni. Requis pour envoyer une demande.",
      sentOn: (file, date) => `${file} · envoyé le ${date}`,
      approved: (file) => `${file} · validé`,
      approvedUntil: (file, date) => `${file} · validé · expire le ${date}`,
      expiringSoon: "Expire bientôt",
      expired: "Expiré",
      renewHint: "Envoyez votre document renouvelé avant son expiration : les loueurs ne peuvent pas accepter une demande avec un document expiré.",
      wrongDate: "Date d'expiration erronée ? Corrigez-la dans votre profil",
      rejected: (note) => `Refusé : ${note}`,
      rejectedNoNote: "Refusé. Ajoutez un nouveau document.",
      upload: "Téléverser",
      replace: "Remplacer",
      uploading: "Envoi…",
      view: "Voir",
    },
  },

  company: {
    header: {
      addVehicle: "Ajouter un véhicule",
    },
    nav: {
      aria: "Espace loueur",
      overview: "Vue d’ensemble",
      listings: "Mes annonces",
      bookings: "Réservations",
      calendar: "Calendrier",
      customers: "Clients",
      messages: "Messages",
      reviews: "Avis",
      subscription: "Abonnement",
      profile: "Profil société",
      accountProfile: "Profil du compte",
    },
    launch: {
      name: "Accès gratuit au lancement",
      usage: (n, limit) =>
        limit ? `${n} / ${limit} annonces actives · Accès gratuit au lancement` : `${n} annonces actives · Accès gratuit au lancement`,
    },
    mySubscription: {
      title: "Mon abonnement",
      loading: "Chargement de votre abonnement…",
      active: "Actif",
      launchTitle: "Accès gratuit au lancement",
      launchPrice: "0 €/mois · sans carte bancaire",
      activeListings: "Annonces actives",
      usage: (n, limit) => (limit ? `${n} / ${limit}` : `${n} · illimité`),
      launchText: (days) =>
        `Des formules payantes seront proposées plus tard. Vous serez prévenu au moins ${days} jours avant tout changement, et vos annonces ne sont jamais supprimées.`,
      sponsorBadge: "Sponsorisé",
      sponsorTitle: "Option facultative",
      sponsorText: (options) =>
        `${options}. Les annonces sponsorisées apparaissent en tête des résultats. Pas de renouvellement automatique par défaut.`,
      sponsorCta: "Sponsoriser une annonce",
      footnote: "0 % de commission sur vos locations. Loyers et dépôts de garantie sont payés directement par les chauffeurs.",
      plansTitle: "Formules",
      plansText: "Les formules payantes sont en préparation. Contactez-nous pour choisir une formule ; vos annonces restent en ligne en attendant.",
      perMonth: "/mois HT",
      listingLimit: (n) => `${n} annonces actives`,
      contact: "Nous contacter",
    },
    sponsor: {
      badge: "Sponsorisé",
      until: (date) => `Jusqu’au ${date}`,
      sponsor: "Sponsoriser",
      extend: "Prolonger la sponsorisation",
      makeTitle: "Sponsoriser cette annonce",
      extendTitle: "Prolonger la sponsorisation",
      text: "Les annonces sponsorisées affichent un badge Sponsorisé et apparaissent en tête des résultats.",
      option: (days, price) => `${days} jours — ${price}`,
      untilDate: (date) => `jusqu’au ${date}`,
      activeUntil: (date) => `Sponsorisée jusqu’au ${date}`,
      ok: "Sponsoriser l’annonce",
      extendOk: "Prolonger",
      note: "Paiement unique, prix HT. Pas de renouvellement automatique.",
      unavailable: "Les annonces sponsorisées ne sont pas disponibles pour le moment.",
      redirecting: "Ouverture du paiement sécurisé…",
      pay: (amount) => `Payer ${amount}`,
      vatLine: (ht, vat, ttc) => `${ht} HT + ${vat} TVA (20 %) = ${ttc}. Paiement sécurisé par Stripe.`,
      paid: (date) => `Paiement reçu — sponsorisée jusqu’au ${date}`,
      cancelled: "Paiement annulé. Votre annonce n’a pas été sponsorisée.",
    },
    sidebar: {
      usage: (n, limit, plan) => `${n} / ${limit} annonces actives · Formule ${plan}`,
      noPlan: "Aucune formule active",
    },
    banners: {
      incomplete: {
        title: "Complétez votre compte pour être vérifié.",
        text: "Ajoutez les informations de votre société et vos documents. Vous pouvez préparer des annonces en brouillon en attendant.",
        cta: "Compléter la vérification",
      },
      pending: {
        title: "Vérification en cours.",
        text: "Vous pouvez préparer des annonces en brouillon ; la publication sera possible une fois votre dossier (SIRET, Kbis, assurance) validé.",
      },
      rejected: {
        title: "Vérification refusée.",
        text: "Mettez à jour vos documents pour soumettre à nouveau votre dossier.",
        cta: "Mettre à jour mes documents",
      },
      noPlan: {
        title: "Société vérifiée.",
        text: "Choisissez une formule pour démarrer vos 30 jours d’essai gratuit et publier vos véhicules.",
        cta: "Choisir une formule",
      },
      pastDue: {
        title: "Paiement en retard.",
        text: (date) =>
          `Le paiement du ${date} a échoué. Vos annonces restent en ligne mais la publication de nouveaux véhicules est bloquée.`,
        cta: "Mettre à jour le moyen de paiement",
      },
      trial: {
        title: (days) => `Essai gratuit — ${days} jours restants.`,
        text: (date) => `Se termine le ${date}. Ajoutez un moyen de paiement pour continuer sans interruption.`,
        cta: "Ajouter un moyen de paiement",
      },
      inactive: {
        title: { cancelled: "Abonnement résilié.", expired: "Abonnement expiré." },
        text: "Vos annonces et vos données sont conservées, mais vous ne pouvez pas publier de nouveaux véhicules.",
        cta: "Réactiver",
      },
    },
    overview: {
      title: "Vue d’ensemble",
      kpis: {
        activeListings: "Annonces actives",
        available: "Disponibles",
        activeRentals: "Locations en cours",
        newRequests: "Nouvelles demandes",
        listingLimit: "Limite d’annonces",
        noLimit: "Sans limite",
      },
      actions: {
        addVehicle: "Ajouter un véhicule",
        viewRequests: "Voir les demandes",
        manageSubscription: "Gérer mon abonnement",
      },
      queue: {
        title: "Demandes de réservation à traiter",
        received: (date) => `reçue le ${date}`,
        empty: "Aucune demande en attente.",
      },
      movements: {
        title: "Prochains départs et retours",
        pickup: "Départ",
        return: "Retour",
        toAssign: "à attribuer",
        empty: "Rien de prévu dans les 14 prochains jours.",
      },
    },
    docSummary: {
      blocking: (n) => `${n} ${plural(n, "manquant ou expiré", "manquants ou expirés")}`,
      review: (n) => `${n} en cours de vérification`,
      approved: (n) => `${n}/${n} ${plural(n, "validé", "validés")}`,
    },
    visibility: {
      visible: "Visible sur la place de marché",
      hidden: (reason) => `Non visible · ${reason}`,
      reasons: {
        draft: "brouillon",
        paused: "en pause",
        moderated: "en modération",
        archived: "archivée",
        notVerified: "société non vérifiée",
        noPlan: "aucune formule active",
      },
    },
    errors: {
      mustBeVerified: "votre société doit être vérifiée avant de publier.",
      choosePlan: "choisissez une formule avant de publier.",
      limit: (plan, n) => `Votre formule ${plan} comprend ${n} annonces actives.`,
      codeRequired: "Saisissez un code interne.",
      codeExists: (code) => `${code} existe déjà dans votre flotte.`,
      invalidPeriod: "Période invalide.",
      overlap: (ref) => `Une réservation confirmée (${ref}) chevauche ces dates.`,
      notFree: (code) => `${code} n’est pas libre à ces dates.`,
      assignFirst: "Attribuez d’abord un véhicule.",
      blocked: "Bloqué",
      urgentInvoice: (days) => `Urgent ${days} jours`,
    },
    publishFlow: {
      savedAsDraft: (reason) => `Enregistrée en brouillon — ${reason}`,
      published: "Annonce publiée",
    },
    listings: {
      title: "Mes annonces",
      availableToday: "disponibles aujourd’hui",
      urgentActive: "URGENT ACTIF",
      expiresOn: (date) => `Expire le ${date}`,
      makeUrgent: "Passer en Urgent",
      extend: "Prolonger",
      pause: "Mettre en pause",
      paused: "Annonce mise en pause",
      archive: "Archiver",
      archiveTitle: "Archiver cette annonce ?",
      archiveText: "Elle sera retirée de la place de marché. L’historique des réservations est conservé.",
      archived: "Annonce archivée",
      emptyTitle: "Aucun véhicule pour l’instant",
      emptyText: "Créez une annonce par modèle, puis ajoutez vos véhicules physiques.",
      notFound: "Cette annonce n’existe pas.",
      loading: "Chargement de vos annonces…",
      delete: "Supprimer",
      deleteTitle: "Supprimer ce brouillon ?",
      deleteText: "L’annonce, ses photos et ses véhicules physiques seront définitivement supprimés.",
    },
    editor: {
      tabs: {
        info: "Informations",
        photos: "Photos",
        terms: "Tarifs et conditions",
        docs: "Documents requis",
        units: "Véhicules physiques",
        preview: "Aperçu",
      },
      editTitle: (name) => `Modifier · ${name}`,
      addTitle: "Ajouter un véhicule",
      amountRequired: "Montant obligatoire",
      unitsRequired: "Ajoutez au moins un véhicule physique.",
      saved: "Modifications enregistrées",
      published: "Véhicule publié",
      fields: {
        make: "Marque",
        model: "Modèle",
        version: "Version",
        year: "Année",
        fuel: "Énergie",
        gearbox: "Boîte de vitesses",
        category: "Catégorie",
        pickup: "Lieu de prise en charge",
        seats: "Places",
        colour: "Couleur",
        mileage: "Kilométrage moyen (km)",
        description: "Description",
        daily: "Prix par jour (€)",
        weekly: "Tarif hebdomadaire (€)",
        monthly: "Tarif mensuel (€)",
        deposit: "Caution (€)",
        minDays: "Durée minimale (jours)",
        kmIncluded: "Km inclus par semaine",
        extraKm: "Prix du km supplémentaire (€)",
        insurance: "Assurance",
        maintenance: "Entretien",
      },
      address: {
        title: "Adresse de prise en charge",
        hint: "Les chauffeurs ne voient publiquement que la ville. L'adresse complète est communiquée au chauffeur une fois sa demande acceptée.",
        search: "Rechercher une adresse",
        searchPlaceholder: "Commencez à taper, ex. 15 Avenue de la République",
        street: "Numéro et nom de rue",
        postalCode: "Code postal",
        city: "Ville",
        country: "Pays",
        saved: "Adresse enregistrée",
        publicCity: "Ville affichée dans la recherche",
      },
      photosHint: "Ajoutez jusqu’à 5 photos. La première sert de photo principale.",
      mainPhoto: "Photo principale ✓",
      photoN: (n) => `Photo ${n} ✓`,
      addPhoto: "+ Ajouter une photo",
      photoTooLarge: "Chaque photo doit faire 10 Mo maximum.",
      weeklyAuto: "Calculé à partir du prix par jour (× 7). Vous pouvez le modifier, par exemple pour proposer une remise à la semaine.",
      monthlyAuto: "Calculé à partir du tarif hebdomadaire (× 52 ÷ 12). Vous pouvez le modifier, par exemple pour proposer une remise au mois.",
      photoType: (name) => `« ${name} » n'est pas une image acceptée. Utilisez une photo JPG, PNG ou WebP.`,
      next: "Suivant",
      saving: "Enregistrement…",
      docsHint: "Documents que le chauffeur doit fournir avant d’envoyer une demande.",
      unitsHint:
        "Une annonce publique peut regrouper plusieurs véhicules identiques. La disponibilité affichée aux chauffeurs est calculée à partir de ces véhicules physiques.",
      codePlaceholder: "Code interne (ex. TM3-008)",
      code: "Code interne",
      registration: "Immatriculation",
      addUnit: "+ Ajouter un véhicule physique",
      previewHint: "Aperçu de l’annonce telle qu’elle apparaîtra sur la place de marché.",
      from: "À partir de",
      fleetCount: (n) => `${n} ${plural(n, "véhicule", "véhicules")} dans la flotte`,
      termsHint: "Les demandes déjà envoyées conservent les conditions en vigueur au moment de l’envoi.",
      saveChanges: "Enregistrer les modifications",
      saveDraft: "Enregistrer le brouillon",
      publishVehicle: "Publier le véhicule",
    },
    listingDetail: {
      viewPublic: "Voir l’annonce publique",
      pickup: "Adresse de prise en charge",
      stats: {
        units: "Véhicules physiques",
        availableToday: "Disponibles aujourd’hui",
        availableRange: (from, to) => `Disponibles ${from} → ${to}`,
        inMaintenance: "En entretien",
      },
      unitsTitle: "Véhicules physiques",
      unitsHint: "Un véhicule ne peut pas être attribué à deux réservations qui se chevauchent.",
      cols: {
        code: "Code",
        registration: "Immatriculation",
        mileage: "Kilométrage",
        status: "Statut",
        occupation: "Occupation",
      },
      rented: (id, end) => `Loué · ${id} jusqu’au ${end}`,
      booked: (start, end, id) => `Réservé ${start} → ${end} · ${id}`,
      blocked: (start, end, reason) => `Bloqué ${start} → ${end} · ${reason}`,
      free: "Libre",
      added: (code) => `${code} ajouté à la flotte`,
      backInService: (code) => `${code} remis en service`,
      nowInMaintenance: (code) => `${code} en entretien`,
      returnToService: "Remettre en service",
      maintenance: "Entretien",
      blockDates: "Bloquer des dates",
      codePlaceholder: (code) => `Code (ex. ${code})`,
      addUnit: "Ajouter un véhicule physique",
      bookingsTitle: "Réservations de cette annonce",
      notAssigned: "Non attribué",
      noBookings: "Aucune réservation pour l’instant.",
    },
    bookings: {
      title: "Demandes de réservation",
      tabs: {
        new: "Nouvelles",
        documents: "Documents requis",
        confirmed: "Confirmées",
        active: "En cours",
        history: "Historique",
      },
      cols: {
        reference: "Référence",
        driver: "Chauffeur",
        vehicle: "Véhicule",
        dates: "Dates",
        documents: "Documents",
        status: "Statut",
      },
      empty: "Aucune demande dans cette catégorie.",
    },
    booking: {
      notFound: "Cette réservation n’existe pas.",
      title: "Demande",
      received: (date) => `Reçue le ${date}`,
      requestCard: "Demande",
      vehicle: "Véhicule",
      pickup: "Adresse de prise en charge",
      period: "Période",
      periodValue: (from, to, days) => `${from} → ${to} (${days} jours)`,
      rate: "Tarif (figé)",
      estimate: "Prix estimé",
      deposit: "Caution",
      kmIncluded: "Kilométrage inclus",
      kmValue: (km) => `${km} km / semaine`,
      availableForPeriod: "Disponibles sur la période",
      driverMessage: "Message du chauffeur",
      driverMeta: (city, joined, year) => `${city} · inscrit le ${joined} · permis depuis ${year}`,
      contactHidden: "Coordonnées visibles après acceptation de la demande.",
      driverDocs: "Documents du chauffeur",
      validUntil: (date) => `Valide jusqu’au ${date}`,
      notProvided: "Non fourni",
      docsHint: "Accès réservé aux loueurs concernés par une demande. Chaque consultation est enregistrée.",
      messages: "Messages",
      noMessages: "Aucun message.",
      writeToDriver: "Écrire au chauffeur…",
      actions: "Actions",
      assigned: (code, plate) => `Véhicule attribué : ${code} · ${plate}`,
      deferred: "Attribution différée — aucun véhicule physique attribué pour l’instant.",
      waiting: "En attente de la réponse du chauffeur.",
      accept: "Accepter",
      requestInfo: "Demander des informations",
      decline: "Refuser",
      neverAuto: "Une demande n’est jamais confirmée automatiquement.",
      changeVehicle: "Changer de véhicule",
      assignVehicle: "Attribuer un véhicule",
      handover: "Véhicule remis au chauffeur",
      rentalStarted: "Location démarrée",
      closeRental: "Clôturer la location",
      rentalClosed: "Location clôturée",
      noActions: "Aucune action possible pour ce statut.",
      history: "Historique",
      byDriver: "Chauffeur",
      you: "Vous",
      platform: "Plateforme",
      docPreview: (file) => `Aperçu confidentiel · ${file}`,
    },
    vehicleModal: {
      acceptTitle: (id) => `Accepter la demande ${id}`,
      assignTitle: "Attribuer un véhicule",
      noneFree:
        "Aucun véhicule physique n’est libre à ces dates. Vous pouvez accepter avec attribution différée ou refuser la demande.",
      choose: (from, to) =>
        `Choisissez le véhicule physique pour la période du ${from} au ${to}. Les véhicules déjà réservés sur des dates qui se chevauchent sont bloqués.`,
      acceptOk: "Accepter et confirmer",
      assignOk: "Attribuer",
      chooseOrDefer: "Choisissez un véhicule ou l’attribution différée.",
      chooseAvailable: "Choisissez un véhicule disponible.",
      accepted: "Demande acceptée — réservation confirmée",
      assigned: "Véhicule attribué",
      inMaintenance: "En entretien",
      alreadyBooked: (from, to, ref) => `Déjà réservé ${from} → ${to} (${ref})`,
      blocked: (reason) => `Bloqué : ${reason}`,
      available: (plate) => `Disponible · ${plate}`,
      later: "Attribuer le véhicule plus tard",
      messagePlaceholder: "Message au chauffeur (facultatif) : lieu et heure de prise en charge…",
    },
    messageModal: {
      declineTitle: "Refuser la demande",
      infoTitle: "Demander des informations",
      declineText: "Indiquez le motif du refus. Il sera envoyé au chauffeur.",
      infoText:
        "Précisez les documents ou informations nécessaires. La demande passera en « Documents requis ».",
      declineOk: "Refuser la demande",
      infoOk: "Envoyer la demande",
      required: "Un message est obligatoire.",
      declined: "Demande refusée",
      infoSent: "Demande d’informations envoyée",
      declinePlaceholder: "Motif du refus…",
      infoPlaceholder: "Ex. merci d’envoyer une carte VTC valide.",
    },
    calendar: {
      title: "Calendrier des disponibilités",
      previous: "Mois précédent",
      next: "Mois suivant",
      months: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      legend: {
        confirmed: "Confirmée",
        active: "Location en cours",
        blocked: "Bloqué",
        maintenance: "Entretien",
      },
      hint: "Cliquez sur un véhicule pour bloquer des dates.",
      vehicle: "Véhicule",
      empty: "Aucun véhicule physique pour l’instant.",
      unassigned: (n) =>
        n <= 1
          ? `${n} demande en attente n’est pas encore attribuée à un véhicule et n’apparaît pas dans le calendrier.`
          : `${n} demandes en attente ne sont pas encore attribuées à un véhicule et n’apparaissent pas dans le calendrier.`,
    },
    customers: {
      title: "Clients",
      contactHidden: "coordonnées après acceptation",
      bookings: (n) => `${n} ${plural(n, "réservation", "réservations")}`,
      latest: "Dernière demande",
      empty: "Aucun client pour l’instant. Les chauffeurs apparaissent ici après leur première demande.",
    },
    messages: {
      title: "Messages",
      empty: "Aucune conversation.",
      select: "Sélectionnez une conversation",
      placeholder: "Écrire un message…",
    },
    subscription: {
      title: "Mon abonnement",
      currentPlan: "Formule actuelle",
      planName: (name) => `Formule ${name}`,
      perMonthExclVat: "/mois HT",
      activeListings: "annonces actives",
      limitReached: "Limite atteinte",
      remaining: (n) => `${n} ${plural(n, "restante", "restantes")}`,
      trialEnds: "Fin de l’essai",
      nextBilling: "Prochaine facturation",
      paymentMethod: "Moyen de paiement",
      none: "Aucun",
      commission: "Commission sur les locations",
      updatePayment: "Mettre à jour le moyen de paiement",
      downloadInvoices: "Télécharger mes factures",
      downloading: "Téléchargement des factures (démo)",
      cancel: "Résilier mon abonnement",
      reactivate: "Réactiver mon abonnement",
      reactivated: "Abonnement réactivé",
      invoices: "Factures",
      inclVat: (amount) => `${amount} TTC`,
      noInvoices: "Aucune facture pendant l’essai gratuit.",
      notVerified: "Vous pourrez choisir une formule une fois votre société vérifiée par notre équipe.",
      changePlan: "Changer de formule",
      choosePlan: "Choisir une formule",
      currentBadge: "Formule actuelle",
      listingCount: (n) => `${n} annonces actives`,
      startTrial: "Démarrer l’essai gratuit",
      startFree: "Commencer gratuitement",
      switchTo: (name) => `Passer à ${name}`,
      trialActivated: (name) => `Essai gratuit ${name} activé — 30 jours`,
      freeActivated: "Formule gratuite activée",
      planActivated: (name) => `Formule ${name} activée`,
      urgentTitle: "Option en supplément de votre formule",
      urgentText: (week, month) =>
        `7 jours — ${week} · 30 jours — ${month}. Annonce mise en avant en tête des résultats. Pas de renouvellement automatique par défaut.`,
      makeUrgent: "Passer une annonce en Urgent",
      footer:
        "Abonnement fixe. 0 % de commission sur vos locations. Loyers et cautions sont payés directement par les chauffeurs. Changer de formule ne supprime jamais vos annonces.",
      paymentTitle: "Mettre à jour le moyen de paiement",
      saveCard: "Enregistrer la carte",
      cardNumber: "Numéro de carte",
      expiry: "Expiration",
      paymentHint: "Paiement de l’abonnement uniquement — simulation, aucun prestataire de paiement connecté.",
      paymentUpdated: "Moyen de paiement mis à jour",
      cancelTitle: "Résilier mon abonnement ?",
      cancelText:
        "Vos annonces et votre historique sont conservés, mais vous ne pourrez plus publier de nouveaux véhicules. Vous pouvez réactiver à tout moment.",
      keep: "Garder mon abonnement",
      cancelled: "Abonnement résilié — vos annonces sont conservées",
      downgradeTitle: (name) => `Passer à ${name} ?`,
      downgradeText: (usage, limit) =>
        `Vous avez ${usage} annonces actives pour une limite de ${limit}. Aucune annonce ne sera supprimée, mais vous ne pourrez pas publier de nouveaux véhicules tant que vous dépassez la limite.`,
      confirmChange: "Confirmer le changement",
      planChanged: "Formule modifiée",
    },
    profile: {
      title: "Profil société",
      fields: {
        name: "Raison sociale",
        siret: "SIRET",
        vat: "N° de TVA",
        address: "Adresse",
        city: "Ville",
        postalCode: "Code postal",
        region: "Région",
        country: "Pays",
        firstName: "Prénom",
        lastName: "Nom",
        phone: "Téléphone",
        email: "E-mail",
      },
      saved: "Profil enregistré",
      update: "Mettre à jour le profil",
      updating: "Mise à jour…",
      hint: "Le téléphone et l’e-mail ne sont communiqués aux chauffeurs qu’après acceptation d’une demande.",
      verification: "Vérification",
      users: "Utilisateurs",
      usersText: (who) =>
        `${who} (administrateur). Comptes d’équipe : bientôt disponibles — jusqu’à 3 avec Pro, 8 avec Business, 20 avec Flotte.`,
      accountInfo: "Informations du compte",
      createdOn: (date) => `Informations fournies à la création du compte le ${date}.`,
      accountType: "Type de compte",
      businessType: "Type d’activité",
      publicProfile: "Profil public et adresse",
      companyDetails: "Informations de la société",
      displayName: "Nom affiché",
      vatOptional: "N° de TVA (facultatif)",
      requiredForVerification: (fields) => `Obligatoire pour la vérification : ${fields}`,
      save: "Enregistrer",
      saving: "Enregistrement…",
      verificationSubtitle: (owner, business) => `${owner} · ${business}`,
      upload: "Téléverser",
      replace: "Remplacer",
      uploading: "Envoi…",
      uploadThenSubmit: "Téléversez chaque document, puis envoyez.",
      submitForVerification: "Envoyer pour vérification",
      submitting: "Envoi…",
      submittedOn: (date) => `Envoyé le ${date}. Notre équipe répond généralement sous 1 jour ouvré.`,
      verifiedText: "Votre compte est vérifié. Vous pouvez publier vos véhicules.",
      rejectedText: "Votre dossier a été refusé. Remplacez les documents concernés, puis renvoyez-le.",
      reviewNote: (note) => `Note de l’équipe : ${note}`,
      convertTitle: "Vous louez en tant que professionnel ?",
      convertText: "Passez à un compte Société pour louer sous le nom de votre entreprise, avec SIRET et vérification de la société.",
      convertButton: "Passer à un compte société",
    },
    convert: {
      back: "Profil société",
      title: "Passer à un compte société",
      intro:
        "Vos annonces et réservations sont conservées. Votre compte devient tout de suite un compte Société ; notre équipe vérifie ensuite votre société avant que vous puissiez publier de nouveaux véhicules.",
      steps: [
        { title: "Informations de la société", text: "Raison sociale, SIRET, numéro de TVA et adresse du siège." },
        { title: "Type d’activité", text: "Location de voitures VTC ou société de location classique." },
        { title: "Documents de vérification", text: "Kbis, assurance et autres documents requis pour votre activité." },
        { title: "Vérification", text: "Vos annonces en ligne le restent pendant la vérification de la société." },
      ],
      start: "Commencer",
      companyInfo: "Informations de la société",
      siretPlaceholder: "912 345 678 00021",
      vatPlaceholder: "FR00 000000000",
      headOffice: "Adresse du siège",
      businessType: "Type d’activité",
      documents: "Documents de vérification",
      requiredFor: (type) => `Requis pour : ${type}.`,
      chooseTypeFirst: "Choisissez un type d’activité pour voir les documents requis.",
      submit: "Envoyer pour vérification",
      submitting: "Envoi…",
      cancel: "Annuler",
      missing: (n) => `${n} document(s) manquant(s)`,
      siretInvalid: "Le SIRET doit comporter 14 chiffres",
    },
    modals: {
      upgradeTitle: "Vous avez atteint la limite de votre abonnement.",
      upgradeText: (plan, limit, live) =>
        `Votre formule ${plan} comprend ${limit} annonces actives (${live} en ligne). Vos annonces existantes restent en ligne.`,
      upgradeOk: (plan, price) => `Passer à ${plan} — ${price}/mois`,
      later: "Plus tard",
      urgentExtendTitle: "Prolonger Urgent",
      urgentMakeTitle: "Passer en Urgent",
      urgentText: "Augmentez la visibilité de votre véhicule auprès des chauffeurs VTC.",
      urgentExtendOk: "Prolonger",
      urgentActivateOk: "Activer Urgent",
      urgentActivated: (date) => `URGENT ACTIF — expire le ${date}`,
      urgentActiveUntil: (date) => `URGENT ACTIF · expire le ${date}`,
      urgentOption: (days, price) => `${days} jours — ${price}`,
      until: (date) => `jusqu’au ${date}`,
      autoRenew: "Renouveler automatiquement à l’échéance",
      payNote: (card) =>
        `Paiement unique débité sur ${card}. Pas de renouvellement automatique sauf si vous cochez la case. Simulation, aucun prestataire de paiement connecté.`,
      savedCard: "votre carte enregistrée",
      blockTitle: (code) => `Bloquer ${code}`,
      blockText:
        "Le véhicule sera indisponible pendant cette période. Impossible si une réservation confirmée chevauche ces dates.",
      blockOk: "Bloquer ces dates",
      blocked: "Dates bloquées",
      from: "Du",
      to: "Au",
      reasonPlaceholder: "Motif (révision, carrosserie…)",
    },
    comingSoon: {
      title: "Bientôt disponible",
      notBuilt: " n’est pas encore disponible.",
    },
    status: {
      booking: {
        pending: "En attente",
        documents_required: "Documents requis",
        confirmed: "Confirmée",
        active: "Location en cours",
        completed: "Terminée",
        declined: "Refusée",
        cancelled: "Annulée",
        expired: "Expirée",
      },
      publish: {
        draft: "Brouillon",
        published: "Publiée",
        paused: "En pause",
        moderated: "En modération",
        archived: "Archivée",
      },
      unit: {
        active: "En service",
        maintenance: "Entretien",
        retired: "Retiré",
      },
      driverDoc: {
        approved: "Validé",
        pending: "En vérification",
        missing: "Manquant",
        expired: "Expiré",
      },
      subscription: {
        trial: "Essai gratuit",
        active: "Actif",
        past_due: "Paiement en retard",
        cancelled: "Résilié",
        expired: "Expiré",
      },
      verification: {
        incomplete: "Vérification à compléter",
        pending: "Vérification en attente",
        verified: "Vérifiée",
        rejected: "Refusée",
      },
      companyDoc: {
        approved: "Validé",
        pending: "En vérification",
        rejected: "Refusé",
        missing: "Manquant",
      },
      driver: {
        verified: "Vérifié",
        pending: "En attente",
      },
      invoice: {
        Paid: "Payée",
        Failed: "Échouée",
      },
    },
    plans: {
      free: { name: "Gratuit", features: ["Demandes de réservation", "Calendrier des disponibilités", "1 utilisateur"] },
      pro: { name: "Pro", features: ["Tout Gratuit", "Messagerie avec les chauffeurs", "Jusqu’à 3 utilisateurs"] },
      business: { name: "Business", features: ["Tout Pro", "Support prioritaire", "Jusqu’à 8 utilisateurs"] },
      fleet: { name: "Flotte", features: ["Tout Business", "Chargé de compte dédié", "Jusqu’à 20 utilisateurs"] },
    },
    docTypes: {
      vtc: "Carte VTC",
      licence: "Permis de conduire",
      id: "Pièce d’identité",
      address: "Justificatif de domicile",
    },
  },
};
